import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { showUpgradeExternally } from "./UpgradePrompt";
import { askForLogin } from "../lib/askLogin";
import { askForPremium } from "../lib/askPremium";
import { useCaptions, useActiveLine } from "./Captions";
import { BAR_MIN, BAR_MAX, DEFAULT_STYLE, defaultBarSize } from "../lib/captions";
import "./captionbar.css";
import { publishLights, publishBeat, onLightsPanelRequest } from "../lib/lightsBus";
import { getTvScreenHost, setVideoHome, subscribeTvScreen } from "../lib/tvScreen";
import { Shuffle, Repeat, PlayCircle } from "lucide-react";
import { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r < 10 ? "0" : ""}${r}`;
};

// ---- Steel button faces ----
// Every symbol is "pierced" through its steel disc like jeweler's fretwork: the disc is masked with the
// symbol cut out, and the cyan light behind the disc shows through the cut (styles: index.css, .steel-btn).
// Shapes are drawn on a 24 x 24 grid.
const GLYPHS = {
  play: '<polygon points="8.5,5 19,12 8.5,19" stroke-width="1"/>',
  pause: '<rect x="7" y="5.5" width="3.6" height="13" stroke-width="0.8"/><rect x="13.4" y="5.5" width="3.6" height="13" stroke-width="0.8"/>',
  prev: '<rect x="5.8" y="5.5" width="2.8" height="13" stroke-width="0.8"/><polygon points="18.5,5.5 9.8,12 18.5,18.5" stroke-width="1"/>',
  next: '<rect x="15.4" y="5.5" width="2.8" height="13" stroke-width="0.8"/><polygon points="5.5,5.5 14.2,12 5.5,18.5" stroke-width="1"/>',
  shuffle: '<g transform="translate(12 12) scale(.8) translate(-12 -12)"><path d="M3 7h3c3.4 0 5 2.2 6.6 5s3.2 5 6.6 5H20" fill="none" stroke-width="2.4"/><path d="M3 17h3c1.7 0 2.9-.6 4-1.7M13.2 8.7C14.3 7.6 15.7 7 17.6 7H20" fill="none" stroke-width="2.4"/><polygon points="19.5,3.2 24,7 19.5,10.8" stroke-width="0.8"/><polygon points="19.5,13.2 24,17 19.5,20.8" stroke-width="0.8"/></g>',
  repeat: '<g transform="translate(12 12) scale(.82) translate(-12 -12)"><path d="M4.5 11.5V10a3 3 0 0 1 3-3H19" fill="none" stroke-width="2.4"/><polygon points="17,3.2 22,7 17,10.8" stroke-width="0.8"/><path d="M19.5 12.5V14a3 3 0 0 1-3 3H5" fill="none" stroke-width="2.4"/><polygon points="7,13.2 2,17 7,20.8" stroke-width="0.8"/></g>',
  autoplay: '<circle cx="12" cy="12" r="7.8" fill="none" stroke-width="2"/><polygon points="10,8.6 15.8,12 10,15.4" stroke-width="0.8"/>',
  settings: '<circle cx="12" cy="12" r="5.2" fill="none" stroke-width="2.2"/><path d="M12 2.5v3.2M12 18.3v3.2M2.5 12h3.2M18.3 12h3.2M5.3 5.3l2.3 2.3M16.4 16.4l2.3 2.3M18.7 5.3l-2.3 2.3M7.6 16.4l-2.3 2.3" fill="none" stroke-width="2.2"/>',
  volume: '<polygon points="3.5,9.5 7.5,9.5 12.5,5 12.5,19 7.5,14.5 3.5,14.5" stroke-width="0.8"/><path d="M15.6 9.2a4.2 4.2 0 0 1 0 5.6M18.4 6.6a8 8 0 0 1 0 10.8" fill="none" stroke-width="2"/>',
  mute: '<polygon points="3.5,9.5 7.5,9.5 12.5,5 12.5,19 7.5,14.5 3.5,14.5" stroke-width="0.8"/><path d="M16 9.5l5 5M21 9.5l-5 5" fill="none" stroke-width="2.2"/>',
  maximize: '<path d="M14 4.5h5.5V10M10 19.5H4.5V14M19.5 4.5l-6.3 6.3M4.5 19.5l6.3-6.3" fill="none" stroke-width="2.4"/>',
  minimize: '<path d="M20 9.5h-5.5V4M4 14.5h5.5V20M14.5 9.5l6-6M9.5 14.5l-6 6" fill="none" stroke-width="2.4"/>',
  close: '<path d="M6 6l12 12M18 6L6 18" fill="none" stroke-width="2.8"/>',
  // full screen: four corner marks pointing out (go full screen) or in (come back)
  fullscreen: '<path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" fill="none" stroke-width="2.6"/>',
  exitfs: '<path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" fill="none" stroke-width="2.6"/>',
  // Fit / Fill (edge-to-edge picture): "fit" = the whole picture inside a frame, "fill" = arrows out to the corners
  fit: '<rect x="3.5" y="5.5" width="17" height="13" fill="none" stroke-width="2.2"/><rect x="8" y="9" width="8" height="6" stroke-width="0.8"/>',
  fill: '<rect x="3.5" y="5.5" width="17" height="13" fill="none" stroke-width="2.2"/><path d="M8.6 15.4L15.4 8.6" fill="none" stroke-width="2"/><polygon points="12.4,8.6 16.4,8.6 16.4,12.6" stroke-width="0.8"/><polygon points="11.6,15.4 7.6,15.4 7.6,11.4" stroke-width="0.8"/>',
  // TV mode (a television set with a play triangle on its screen)
  tv: '<rect x="3" y="7" width="18" height="12.5" rx="1.5" fill="none" stroke-width="2.2"/><path d="M8 3l4 3.6L16 3" fill="none" stroke-width="2"/><polygon points="10.2,10.6 15,13.3 10.2,16" stroke-width="0.8"/>',
  // captions (a screen with the letters CC)
  cc: '<rect x="3" y="5.5" width="18" height="13" rx="2.2" fill="none" stroke-width="2.2"/><path d="M10.4 10.6a2.2 2.2 0 1 0 0 2.8M16.9 10.6a2.2 2.2 0 1 0 0 2.8" fill="none" stroke-width="2"/>',
  // song description (a square with the letter i)
  info: '<rect x="3.5" y="3.5" width="17" height="17" rx="2.2" fill="none" stroke-width="2.2"/><circle cx="12" cy="8.3" r="1.4" stroke-width="0.8"/><rect x="10.8" y="11" width="2.4" height="6" stroke-width="0.8"/>',
  // caption settings (two sliders)
  sliders: '<path d="M4 7h9M19 7h1M4 17h1M11 17h9" fill="none" stroke-width="2.4"/><circle cx="16" cy="7" r="2.6" stroke-width="0.8"/><circle cx="8" cy="17" r="2.6" stroke-width="0.8"/>',
  // lights (a light bulb)
  bulb: '<path d="M12 2.8a6.2 6.2 0 0 0-3.7 11.2c.9.7 1.3 1.5 1.3 2.5V17h4.8v-.5c0-1 .4-1.8 1.3-2.5A6.2 6.2 0 0 0 12 2.8z" stroke-width="0.8"/><rect x="9.6" y="18.4" width="4.8" height="1.6" stroke-width="0.8"/><rect x="10.6" y="20.6" width="2.8" height="1.3" stroke-width="0.8"/>',
  // site buttons (header, song page, search button)
  search: '<circle cx="10.3" cy="10.3" r="5.9" fill="none" stroke-width="2.5"/><path d="M14.8 14.8L20.3 20.3" fill="none" stroke-width="3"/>',
  user: '<circle cx="12" cy="8.2" r="3.9" stroke-width="0.8"/><path d="M4.6 20c0-4.2 3.3-6.7 7.4-6.7s7.4 2.5 7.4 6.7z" stroke-width="0.8"/>',
  share: '<circle cx="17.6" cy="5.8" r="3" stroke-width="0.8"/><circle cx="6.4" cy="12" r="3" stroke-width="0.8"/><circle cx="17.6" cy="18.2" r="3" stroke-width="0.8"/><path d="M8.7 10.6L15.3 7.2M8.7 13.4L15.3 16.8" fill="none" stroke-width="2"/>',
  heart: '<path d="M12 20.4C5.2 15.4 3.1 12 3.1 8.8a4.7 4.7 0 0 1 8.9-2 4.7 4.7 0 0 1 8.9 2c0 3.2-2.1 6.6-8.9 11.6z" stroke-width="0.8"/>',
  plus: '<path d="M12 5v14M5 12h14" fill="none" stroke-width="3.2"/>',
  logout: '<path d="M10 4.8H6a1.4 1.4 0 0 0-1.4 1.4v11.6A1.4 1.4 0 0 0 6 19.2h4" fill="none" stroke-width="3"/><path d="M9 10h6V6.2L21.6 12 15 17.8V14H9z" stroke-width="0.8"/>',
};
const CUT_CACHE = {};
const cutFor = (name) => {
  if (!CUT_CACHE[name]) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><defs><mask id="m"><rect width="24" height="24" fill="#fff"/><g fill="#000" stroke="#000" stroke-linejoin="miter" stroke-linecap="square">${GLYPHS[name]}</g></mask></defs><rect width="24" height="24" fill="#000" mask="url(#m)"/></svg>`;
    CUT_CACHE[name] = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }
  return CUT_CACHE[name];
};
// The thin steel sheet has a thickness, and you see it as the little wall around each cut. With the
// light coming from the upper left, the wall on the lower-right side of a cut catches the light
// (bright) and the wall on the upper-left side sits in shadow (dark). This draws just those two
// slivers, inside the cut, as a picture that sits under the steel face. (Only the Pause/Play,
// Previous and Next buttons use it.)
const LIP_CACHE = {};
const lipFor = (name) => {
  if (!LIP_CACHE[name]) {
    const grp = (color, dx, dy) =>
      `<g transform="translate(${dx} ${dy})" fill="${color}" stroke="${color}" stroke-linejoin="miter" stroke-linecap="square">${GLYPHS[name]}</g>`;
    const box = 'maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24"';
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><defs>` +
      `<mask id="h" ${box}><rect width="24" height="24" fill="#000"/>${grp("#fff", 0, 0)}</mask>` +
      `<mask id="a" ${box}><rect width="24" height="24" fill="#fff"/>${grp("#000", -0.7, -0.7)}</mask>` +
      `<mask id="b" ${box}><rect width="24" height="24" fill="#fff"/>${grp("#000", 0.45, 0.45)}</mask>` +
      `</defs><g mask="url(#h)">` +
      `<rect width="24" height="24" fill="#e1edf2" mask="url(#a)"/>` +
      `<rect width="24" height="24" fill="#031116" fill-opacity="0.75" mask="url(#b)"/>` +
      `</g></svg>`;
    LIP_CACHE[name] = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }
  return LIP_CACHE[name];
};
// The same steel pieces are used by the site's own buttons (header, song page, search button), so these
// three are exported. Keeping them here means no extra file to paste.
export function SteelFace({ glyph, bevel = false }) {
  return (
    <span className="steel-wrap" aria-hidden="true">
      {bevel && <span className="steel-lip" style={{ backgroundImage: lipFor(glyph) }} />}
      <span className="steel-face" style={{ "--cut": cutFor(glyph) }} />
    </span>
  );
}

// Share a song. The link goes through the site's share address (/api/share/<song id>): Facebook and texts read the
// song's title and cover art from it, and a person who taps it lands on the song page. A phone gets its own share
// sheet; anywhere else the link is copied. Used by the Share button on the song page and on the big player.
export async function shareSong(video) {
  if (!video || !video.id) return;
  const url = `${window.location.origin}/api/share/${encodeURIComponent(video.id)}`;
  // The text a phone's share sheet fills in. It says the song was made with A.I. assistance, so the disclosure goes along with the link.
  const data = { title: video.title, text: `${video.title} \u2014 ${video.artist} (Made with A.I. Assistance)`, url };
  try {
    if (navigator.share && typeof navigator.canShare === "function" && navigator.canShare(data)) {
      await navigator.share(data);
      return;
    }
  } catch (err) {
    if (err && err.name === "AbortError") return;     // the person closed the share sheet: nothing more to do
    /* anything else: fall through and copy the link */
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  } catch {
    // for places without the clipboard API
    try {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Link copied");
    } catch { toast.error("Could not copy link"); }
  }
}

// Words cut out of a steel plate and lit from behind (the artist's name, "Log in", ...). Four copies of
// the words are stacked: the soft light around them, the empty cut, the light itself, and the thin wall
// of the cut (bright lower-right, dark upper-left). Screen readers get the words once, in plain text.
export function CutLetters({ text }) {
  return (
    <>
      <span className="cn" aria-hidden="true">
        <span className="cn-glow">{text}</span>
        <span className="cn-base">{text}</span>
        <span className="cn-lit">{text}</span>
        <span className="cn-lip">{text}</span>
      </span>
      <span className="sr-only">{text}</span>
    </>
  );
}

// Drawn once on the page (Layout does this): the filter that makes the wall of the cut in CutLetters.
export function SteelDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true" focusable="false">
      <filter id="cn-lip" x="-3%" y="-8%" width="106%" height="116%" colorInterpolationFilters="sRGB">
        <feOffset in="SourceAlpha" dx="-0.8" dy="-0.8" result="ul" />
        <feComposite in="SourceAlpha" in2="ul" operator="out" result="edgeBR" />
        <feFlood floodColor="#e1edf2" result="bc" />
        <feComposite in="bc" in2="edgeBR" operator="in" result="bright" />
        <feOffset in="SourceAlpha" dx="0.55" dy="0.55" result="dr" />
        <feComposite in="SourceAlpha" in2="dr" operator="out" result="edgeUL" />
        <feFlood floodColor="#031116" floodOpacity="0.75" result="dc" />
        <feComposite in="dc" in2="edgeUL" operator="in" result="dark" />
        <feMerge>
          <feMergeNode in="bright" />
          <feMergeNode in="dark" />
        </feMerge>
      </filter>
    </svg>
  );
}

// A phone turned on its side (or any short, wide window). Then the video gets the whole screen
// and the controls sit on top of it, hidden until you touch or hover.
const SIDEWAYS_QUERY = "(orientation: landscape) and (max-height: 520px)";

function useSideways() {
  const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(SIDEWAYS_QUERY).matches;
  const [on, setOn] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(SIDEWAYS_QUERY);
    const update = () => setOn(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);
  return on;
}

// How long the controls stay on screen after the last touch, while a video is playing.
// Full screen: the browser's own full-screen mode, so the address bar and the phone's bars go away.
// The whole page goes full screen (not just the player) so pop-ups and notices still show. A browser
// that cannot do it (an iPhone) simply gets no button.
const fsElement = () => (typeof document === "undefined" ? null : document.fullscreenElement || document.webkitFullscreenElement || null);
// Only the whole page going full screen (this site's own button) counts as "Full screen" here. A plain video on some other
// page, like the small players in Admin, can go full screen by itself: the big player must not close that one again.
const pageFullscreen = () => { const e = fsElement(); return !!e && e === document.documentElement; };
function useFullscreen() {
  const supported = typeof document !== "undefined" && !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
  const [active, setActive] = useState(() => pageFullscreen());
  useEffect(() => {
    const on = () => setActive(pageFullscreen());
    document.addEventListener("fullscreenchange", on);
    document.addEventListener("webkitfullscreenchange", on);
    return () => {
      document.removeEventListener("fullscreenchange", on);
      document.removeEventListener("webkitfullscreenchange", on);
    };
  }, []);
  const run = (fn, target) => {
    if (!fn) return;
    try {
      const r = fn.call(target);
      if (r && typeof r.catch === "function") r.catch(() => {});
    } catch { /* the browser said no; nothing to do */ }
  };
  const enter = useCallback(() => { const el = document.documentElement; run(el.requestFullscreen || el.webkitRequestFullscreen, el); }, []);
  const exit = useCallback(() => { run(document.exitFullscreen || document.webkitExitFullscreen, document); }, []);
  const toggle = useCallback(() => { if (fsElement()) exit(); else enter(); }, [enter, exit]);
  return { supported, active, enter, exit, toggle };
}

const CONTROLS_HIDE_MS = 3000;
// In the sideways / Full screen picture, a video whose shape is within this much of the screen's
// shape fills the screen (0.08 = at most 8% trimmed off one direction, 4% off each edge).
// Anything further off is shown whole, with black strips.
const FILL_MAX_TRIM = 0.08;

// ===========================================================================================
// Mood lights
// A light scheme is {style, colors (1-3), hues [3 color-wheel angles, or 360/361/362 = white/grey/black, or a typed "#rrggbb"], speed (1-5, or 6 = Beat), glow (30-100), reach (1-10, how far a Beat blast travels)}.
// Each mood has one (set in Admin). A song's primary mood picks the lights: the one marked in
// Admin, or the song's first mood. Everyone sees those lights. Logged-in members (free or Premium) can choose the
// song's lights, their own colors, or no lights, and can save named schemes; what they choose is remembered for that song
// and comes back the next time it plays. For a guest, the bulb shows a live sample and the "Create a free login" box.
// A scheme can also carry an "fx" (an effect: frost, flames,
// bats... that shoots out from behind the screen, see FX_LIST and FxLayer). The lights are an LED strip behind the steel frame of the video (Backlight), and the
// steel buttons and plates take the first color (the --lt-* variables, see index.css).
// ===========================================================================================
export const LIGHT_STYLES = [
  { id: "steady", label: "Steady" },
  { id: "pulse", label: "Pulse" },
  { id: "heartbeat", label: "Heartbeat" },
  { id: "maypole-cw", label: "Maypole clockwise" },
  { id: "maypole-ccw", label: "Maypole counter-clockwise" },
];
const REACH_DEFAULT = 6;
export const STANDARD_SCHEME = { style: "steady", colors: 1, hues: [187, 187, 187], speed: 3, glow: 75, reach: REACH_DEFAULT };
// speed steps per style; the fast end of Pulse and Heartbeat is capped so nothing flashes too quickly
// The sixth stop on the speed bar is Beat: the glow follows the music's beat (a Maypole keeps
// turning at the medium speed while it pulses to the beat). Steady has no speed, so no Beat.
const SPEED_FACTOR = {
  steady: [1, 1, 1, 1, 1, 1],
  pulse: [0.5, 0.75, 1, 1.4, 1.8, 1],
  heartbeat: [0.6, 0.8, 1, 1.1, 1.2, 1],
  "maypole-cw": [0.5, 0.75, 1, 1.5, 2.2, 1],
  "maypole-ccw": [0.5, 0.75, 1, 1.5, 2.2, 1],
};
const SPEED_NAMES = ["Very slow", "Slow", "Medium", "Fast", "Very fast", "Beat mode"];
const BEAT = 6;
const BEAT_GAP = 350;       // never more than about 2.8 beats a second, so nothing flashes faster than three times a second
const BEAT_STEADY_PERIOD = 0.6;   // seconds between pulses when the sound cannot be read (about 100 beats a minute)
const maxSpeed = (style) => (style === "steady" || style === "off" ? 5 : BEAT);

// The effects a scheme can carry (the server keeps the same names, LIGHT_FX in server.py). Picking one in the Lights panel
// also sets the matching colors (`preset`), so the glow behind the screen fits the effect; the colors can be changed after.
// `group` says where it sits in the pull-down: "everyday", or a holiday group (see FX_HOLIDAY_BY_MONTH).
export const FX_LIST = [
  { id: "frost", group: "everyday", label: "Frost", preset: { style: "pulse", colors: 2, hues: [195, 215, 215], speed: BEAT, glow: 70, reach: 4 } },
  { id: "flames", group: "everyday", label: "Flames", preset: { style: "pulse", colors: 2, hues: [18, 42, 42], speed: BEAT, glow: 70, reach: 4 } },
  { id: "fireice", group: "everyday", label: "Fire and Ice", preset: { style: "pulse", colors: 2, hues: [200, 20, 20], speed: BEAT, glow: 70, reach: 4 } },
  { id: "blueflame", group: "everyday", label: "Blue Flame", preset: { style: "pulse", colors: 2, hues: [215, 188, 188], speed: BEAT, glow: 70, reach: 4 } },
  { id: "ember", group: "everyday", label: "Ember Drift", preset: { style: "pulse", colors: 2, hues: [24, 38, 38], speed: 1, glow: 50, reach: 3 } },
  { id: "haunted", group: "halloween", label: "Haunted Night", preset: { style: "pulse", colors: 2, hues: [275, 28, 28], speed: BEAT, glow: 65, reach: 4 } },
  { id: "batflight", group: "halloween", label: "Austin Bat Flight", preset: { style: "pulse", colors: 2, hues: [28, 330, 330], speed: BEAT, glow: 60, reach: 4 } },
  { id: "ghosts", group: "halloween", label: "Friendly Ghosts", preset: { style: "pulse", colors: 2, hues: [185, 270, 270], speed: BEAT, glow: 60, reach: 4 } },
];
// The Holiday section of the pull-down changes with the month: month number (1 = January ... 12 = December) -> holiday group.
// A month that is left out has no Holiday section. (Add a group name to FX_GROUP_LABEL, give its effects that `group`, and list its months here.)
const FX_HOLIDAY_BY_MONTH = { 10: "halloween" };
const FX_GROUP_LABEL = { everyday: "Everyday", halloween: "Holiday: Halloween" };
// The sections shown in the pull-down: Everyday, this month's holiday designs, and the section of whatever is picked now
// (so a pick from another season does not vanish from the list).
export function fxSections(selectedId, date = new Date()) {
  const inSeason = FX_HOLIDAY_BY_MONTH[date.getMonth() + 1];
  const picked = FX_LIST.find((f) => f.id === selectedId);
  const groups = ["everyday"];
  if (inSeason) groups.push(inSeason);
  if (picked && !groups.includes(picked.group)) groups.push(picked.group);
  return groups.map((g) => ({ id: g, label: FX_GROUP_LABEL[g] || g, items: FX_LIST.filter((f) => f.group === g) }));
}
const FX_IDS = FX_LIST.map((f) => f.id);

try {
  // the stylesheet already registers it; this is for a browser that skipped that
  if (typeof CSS !== "undefined" && CSS.registerProperty) CSS.registerProperty({ name: "--a", syntax: "<angle>", inherits: false, initialValue: "0turn" });
} catch { /* already registered */ }

const clampInt = (v, lo, hi, dflt) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : dflt;
};

// Make any light scheme (from the server or a form) safe to use.
// Colors that are not on the ring are kept in "hues" as 360 (white), 361 (grey) and 362 (black).
export const NEUTRALS = [
  { id: "white", label: "White", value: 360, rgb: [255, 255, 255] },
  { id: "grey", label: "Grey", value: 361, rgb: [150, 150, 150] },
  { id: "black", label: "Black", value: 362, rgb: [0, 0, 0] },
];
const neutralOf = (h) => NEUTRALS.find((n) => n.value === h) || null;
// A typed color (the admin's color box) is kept in "hues" as "#rrggbb" instead of a wheel angle.
const HEX6 = /^#[0-9a-f]{6}$/i;
const isCode = (h) => typeof h === "string";
const fixHue = (h) => {
  if (isCode(h)) return h.toLowerCase();
  const n = Math.round(h);
  return neutralOf(n) ? n : ((n % 360) + 360) % 360;
};
const readHue = (v, dflt) => {
  if (typeof v === "string" && HEX6.test(v.trim())) return v.trim().toLowerCase();
  return Number.isFinite(Number(v)) && v !== null && v !== "" && typeof v !== "boolean" ? Number(v) : dflt;
};

// The optional fourth color (only used by the Beat blasts, to fill the space between the waves): null = none.
const BLAST_OP_DEFAULT = 60;     // how strong the between-the-waves color is, in percent
const readBlast = (v) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const h = readHue(v, null);
  return h === null ? null : fixHue(h);
};

export function normScheme(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const hs = Array.isArray(r.hues) ? r.hues : [];
  const h0 = readHue(hs[0], 187);
  const h1 = readHue(hs[1], h0);
  const h2 = readHue(hs[2], h1);
  const known = ["off", ...LIGHT_STYLES.map((s) => s.id)];
  const style = known.includes(r.style) ? r.style : "steady";
  return {
    style,
    colors: clampInt(r.colors, 1, 3, 1),
    hues: [h0, h1, h2].map(fixHue),
    speed: clampInt(r.speed, 1, maxSpeed(style), 3),
    glow: clampInt(r.glow, 30, 100, 75),
    reach: clampInt(r.reach, 1, 10, REACH_DEFAULT),
    blast: readBlast(r.blast),
    blastOp: clampInt(r.blastOp, 10, 100, BLAST_OP_DEFAULT),
    fx: FX_IDS.includes(r.fx) ? r.fx : null,
  };
}

const hslToRgb = (h, s, l) => {
  const hh = (((h % 360) + 360) % 360) / 360;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t0) => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [Math.round(f(hh + 1 / 3) * 255), Math.round(f(hh) * 255), Math.round(f(hh - 1 / 3) * 255)];
};
const mixRgb = (c, t, pct) => c.map((v, i) => Math.round(v * (1 - pct) + t[i] * pct));
const toHex = (c) => `#${c.map((v) => `0${v.toString(16)}`.slice(-2)).join("")}`;
const rgbCss = (c) => `rgb(${c.join(",")})`;
const hexToRgb = (x) => [1, 3, 5].map((i) => parseInt(x.slice(i, i + 2), 16));
const hueColor = (h) => {
  if (isCode(h)) return hexToRgb(h);
  const n = neutralOf(h);
  return n ? n.rgb : hslToRgb(h, 1, 0.55);
};
// Read a color the admin typed: "#FFD700", "FFD700", "#fd0", "255 215 0", "255,215,0" or "rgb(255, 215, 0)". Gives "#rrggbb", or null.
export function parseColorCode(text) {
  const t = String(text === null || text === undefined ? "" : text).trim().toLowerCase();
  const hex = t.match(/^(#?)([0-9a-f]{6})$/) || t.match(/^(#)([0-9a-f]{3})$/);
  if (hex) {
    const h = hex[2].length === 3 ? hex[2].split("").map((ch) => ch + ch).join("") : hex[2];
    return `#${h}`;
  }
  const rgb = t.replace(/^rgb\s*\(/, "").replace(/\)$/, "").trim().match(/^(\d{1,3})(?:\s*[,;]\s*|\s+)(\d{1,3})(?:\s*[,;]\s*|\s+)(\d{1,3})$/);
  if (!rgb) return null;
  const v = rgb.slice(1, 4).map(Number);
  return v.every((n) => n <= 255) ? toHex(v) : null;
}
// where on the wheel a color sits (0-359), or null when it is too grey to point at a place on the ring
function wheelAngle(rgb) {
  const [r, g, b] = rgb.map((v) => v / 255);
  const mx = Math.max(r, g, b);
  const d = mx - Math.min(r, g, b);
  if (d < 0.12) return null;
  let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  return Math.round(h < 0 ? h + 360 : h) % 360;
}
export const schemeColor = (sch, i) => hueColor(normScheme(sch).hues[i]);

// The steel buttons and plates take the first color: these are the --lt-* variables index.css uses.
export function themeVars(sch) {
  let c = schemeColor(sch, 0);
  if (c[0] * 0.3 + c[1] * 0.59 + c[2] * 0.11 < 40) c = [96, 98, 104];   // black would vanish on the dark steel, so the buttons use a dark steel grey
  const out = {
    "--lt-rgb": c.join(", "),
    "--lt-l-rgb": mixRgb(c, [255, 255, 255], 0.5).join(", "),
    "--lt-x-rgb": mixRgb(c, [255, 255, 255], 0.8).join(", "),
  };
  [79, 68, 60, 50, 40].forEach((p) => { out[`--lt-w${p}`] = toHex(mixRgb(c, [255, 255, 255], p / 100)); });
  [6, 12, 23, 30, 48, 56, 67, 86].forEach((p) => { out[`--lt-b${p}`] = toHex(mixRgb(c, [0, 0, 0], p / 100)); });
  return out;
}

const COMET = 100;   // length of a chasing light, in degrees
const cometCW = (b, c) => `transparent ${b}deg, ${c} ${b + COMET - 6}deg, transparent ${b + COMET}deg, transparent ${b + 180}deg`;
const cometCCW = (b, c) => { const h = b + 180 - COMET; return `transparent ${b}deg, transparent ${h}deg, ${c} ${h + 6}deg, transparent ${b + 180}deg`; };
export function schemeGradient(raw) {
  const sch = normScheme(raw);
  const n = sch.colors;
  const c = [0, 1, 2].map((i) => rgbCss(schemeColor(sch, i)));
  if (!sch.style.startsWith("maypole")) {
    if (n === 1) return `linear-gradient(${c[0]},${c[0]})`;
    if (n === 2) return `conic-gradient(from var(--a), ${c[0]} 0deg, ${c[1]} 180deg, ${c[0]} 360deg)`;
    return `conic-gradient(from var(--a), ${c[0]} 0deg, ${c[1]} 120deg, ${c[2]} 240deg, ${c[0]} 360deg)`;
  }
  const comet = sch.style === "maypole-ccw" ? cometCCW : cometCW;
  if (n === 1) return `conic-gradient(from var(--a), ${comet(0, c[0])}, ${comet(180, c[0])})`;
  if (n === 2) return `conic-gradient(from var(--a), ${comet(0, c[0])}, ${comet(180, c[1])})`;
  return `conic-gradient(from var(--a), ${[c[0], c[1], c[2], c[0], c[1], c[2], c[0]].join(", ")})`;
}

// ---- Beat: the glow follows the beat of the music ----
// The sound is copied to an analyser that only measures it. It is never connected to the speakers,
// so what you hear is not touched. A beat is a sudden rise in the low (bass) sounds. Because it reacts
// to every beat as it comes, it follows the song's tempo and a change of tempo with no setting.
// Each beat also has a strength (how hard the bass hit, compared with the hardest hits lately). The
// glow flashes, and a blast of light rushes out from the frame like the push from a big speaker: the
// stronger the beat (and the further the member set the "Strength" wedge), the further it travels.
// When the page is not allowed to read the sound (a browser without captureStream, or a video from
// another web address that does not allow it), the glow pulses at a steady tempo instead, and the
// lights panel says so. The little sample screens in the lights editor (and the sample shown to free
// members) use a made-up beat so everyone can see what Beat does.
let beatCtx = null;
// A video's sound can only be copied once at a time: a second copy taken while the first is in use takes the sound away from
// the first, and the first one goes deaf. The player and the little sample in the lights panel both listen to the same
// video, so they share ONE copy (and one analyser, which any number of them may read). The copy is let go of when the last
// one that was using it closes.
const sharedCopies = new WeakMap();   // video element -> {stream, src, an, users}
function listenTo(media) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || !media || typeof media.captureStream !== "function") return { unsupported: true, why: "unsupported" };
    if (!beatCtx) beatCtx = new AC();
    if (beatCtx.state === "suspended") beatCtx.resume().catch(() => {});
    let shared = sharedCopies.get(media);
    if (!shared) {
      const stream = media.captureStream();
      if (!stream.getAudioTracks().length) return null;            // the sound has not started loading yet
      const src = beatCtx.createMediaStreamSource(stream);
      const an = beatCtx.createAnalyser();
      an.fftSize = 1024; an.smoothingTimeConstant = 0.1; an.minDecibels = -90; an.maxDecibels = 0;
      src.connect(an);
      shared = { stream, src, an, users: 0 };
      sharedCopies.set(media, shared);
    }
    shared.users += 1;
    let open = true;
    // closing lets go of the copy once nobody else is using it, so the next song can be captured cleanly
    return { an: shared.an, buf: new Uint8Array(shared.an.frequencyBinCount), close() {
      if (!open) return;
      open = false;
      shared.users -= 1;
      if (shared.users > 0) return;
      if (sharedCopies.get(media) === shared) sharedCopies.delete(media);
      try { shared.src.disconnect(); } catch { /* gone already */ }
      try { shared.stream.getTracks().forEach((t) => t.stop()); } catch { /* gone already */ }
    } };
  } catch { return { unsupported: true, why: "blocked" }; }
}

// the analyser reports each sound's loudness in decibels (-90 to 0 dB, one step is about a third of a dB);
// beats are judged on the real energy, so this table turns a reading back into energy
const ENERGY = Array.from({ length: 256 }, (_, i) => 10 ** ((-90 + (i / 255) * 90) / 10));
const BEAT_FLOOR = 10 ** (-60 / 10);   // a bass quieter than this is not a beat
const BEAT_RISE = 1.4;                  // a beat is the bass getting 40 percent stronger than it has been lately
const BEAT_PEAK_MS = 90;                // the hardest part of a kick comes a moment after it starts; strength is read over this long

export function makeBeatFinder() {
  const hist = [];      // [time, bass energy] for the last 0.9 seconds
  let last = -1e9;
  let heard = false;
  let ref = 0;          // the hardest bass lately, slowly forgotten (about half in four seconds)
  let lastNow = 0;
  let peak = 0;
  return {
    heard: () => heard,
    // how hard the latest beat hit: 1 = as hard as the hardest lately, down to 0.25 for a soft one
    strength() {
      const db = 10 * Math.log10(Math.max(peak / Math.max(ref, 1e-12), 1e-6));
      return Math.max(0.25, Math.min(1, 1 + db / 9));
    },
    step(an, buf, now) {
      an.getByteFrequencyData(buf);
      let low = 0;
      for (let i = 1; i <= 5; i += 1) low += ENERGY[buf[i]];     // about 45 to 230 Hz: kick drum and bass
      low /= 5;
      if (!heard) for (let i = 0; i < 48; i += 1) if (buf[i] > 40) heard = true;     // any real sound at all (silence reads as 0)
      const dt = lastNow ? Math.max(0, Math.min(250, now - lastNow)) : 0;
      lastNow = now;
      ref = Math.max(low, ref * Math.exp(-dt / 5800), BEAT_FLOOR * 4);
      while (hist.length && now - hist[0][0] > 900) hist.shift();
      let avg = 0;
      if (hist.length) { hist.forEach((h) => { avg += h[1]; }); avg /= hist.length; }
      hist.push([now, low]);
      const hit = low > BEAT_FLOOR && low > avg * BEAT_RISE && now - last > BEAT_GAP;
      if (hit) { last = now; peak = low; } else if (now - last < BEAT_PEAK_MS) peak = Math.max(peak, low);
      return hit;
    },
  };
}

// A made-up beat (about 111 a minute: hard, soft, medium, soft) for the sample screens. step(now) returns 0, or the strength of a beat.
const DEMO_PATTERN = [1, 0.45, 0.78, 0.35];
const DEMO_MS = 540;
export function makeDemoBeats() {
  let next = null;
  let n = 0;
  return {
    step(now) {
      if (next === null) next = now + 250;
      if (now < next) return 0;
      next += DEMO_MS;
      if (now > next) next = now + DEMO_MS;     // the page was asleep for a while: carry on from now
      n += 1;
      return DEMO_PATTERN[(n - 1) % DEMO_PATTERN.length];
    },
  };
}

// Beat can only listen to a video that comes from another web address (like the video storage) if that address
// allows it. So for everyone (guests, free members, Premium and admins), in a browser that can listen at all (not an
// iPhone), the player asks for permission when it loads a video (the crossorigin attribute). If the address refuses,
// the browser would not play the video at all, so the player quietly loads it again without asking (it plays; Beat
// just can not hear it) and remembers the refusal for ten minutes so the next songs do not wait.
const refused = new Map();   // web address (origin) -> when it refused
const REFUSED_MS = 10 * 60 * 1000;
const canListen = () => typeof HTMLMediaElement !== "undefined" && typeof HTMLMediaElement.prototype.captureStream === "function";
function originOf(url) { try { return new URL(url, window.location.href).origin; } catch { return ""; } }
function askForSound(url) {
  if (!canListen()) return false;
  const o = originOf(url);
  if (!o || o === "null" || o === window.location.origin) return false;
  const t = refused.get(o);
  return !(t && Date.now() - t < REFUSED_MS);
}

// While a song plays inside the TV's screen on the About page the big player is closed, so nothing is listening to the sound. This listens
// (it shares the one copy of the sound with the big player and the lights panel) and tells the TV about every beat (lib/lightsBus.js), so the
// TV's glow and blasts of light follow the music. When the sound cannot be read, no beats are sent and the TV keeps its steady pulse.
function useTvBeats(enabled, mediaRef, songKey) {
  useEffect(() => {
    const media = mediaRef ? mediaRef.current : null;
    const still = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!enabled || !media || still) return undefined;
    let raf = 0;
    let src = null;
    let finder = makeBeatFinder();
    let lastTry = -1e9;
    let gone = false;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (gone || document.hidden) return;
      if (!src) {
        if (now - lastTry > 500 && !media.paused && media.readyState >= 3 && media.currentTime > 0.05) {
          lastTry = now;
          const got = listenTo(media);
          if (got && got.unsupported) { gone = true; return; }
          src = got;
          finder = makeBeatFinder();
        }
        return;
      }
      if (finder.step(src.an, src.buf, now)) publishBeat(finder.strength());
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); if (src) src.close(); };
  }, [enabled, mediaRef, songKey]);
}

const SHOCKS = 3;          // blasts that can be on screen at once (they last under a second and can not come faster than every 0.35 s)
const SHOCK_PAD = 12;      // the blast rings start this far outside the frame (index.css, .dtm-bl-shock)

// Drives the glow from the music while `running`: the brightness, a small push, and the blasts. Returns
//   status: "music" (hearing the song), "waiting" (just started listening), "blocked" (the sound can not be read, or there is none),
//           "unsupported" (this browser can not listen) or "none" (no video was given, so only a made-up beat can run)
//   mode:   "music" (the song drives it), "demo" (the made-up beat drives it) or "steady" (a steady pulse, set in CSS)
// `cfgRef.current` holds {reach, demo, room}; room = how far (px) the blast may travel {x, y}.
function useBeatGlow(enabled, mediaRef, setRef, shocksRef, running, cfgRef, demo) {
  const [info, setInfo] = useState({ status: "none", mode: "steady" });
  useEffect(() => {
    const media = mediaRef ? mediaRef.current : null;
    const el = setRef.current;
    const still = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!enabled || !el || !running || still) return undefined;
    if (!media && !demo) return undefined;
    const rings = Array.from(shocksRef.current ? shocksRef.current.children : []).map((node) => ({ node, live: false, t0: 0, dur: 1, s: 0, dx: 0, dy: 0, a0: 0, w: 1, h: 1 }));
    let raf = 0;
    let src = null;
    let finder = makeBeatFinder();
    const fake = makeDemoBeats();
    let status = media ? "waiting" : "none";
    let gone = false;
    let lastTry = -1e9;
    let attachedAt = 0;
    let lastT = 0;
    let env = 0;
    let peakEnv = 1;
    let hold = 0;
    let lastHit = 0;
    let gap = 0;          // the time between the last beats; the glow fades faster when the beats come faster
    let latest = null;    // the blast that just started (its strength can still grow for a moment)
    let turn = 0;         // which color leads the next blast
    const modeOf = () => (status === "music" ? "music" : demo ? "demo" : "steady");
    const publish = () => setInfo((old) => (old.status === status && old.mode === modeOf() ? old : { status, mode: modeOf() }));
    publish();
    const attach = (now) => {
      lastTry = now; attachedAt = now; gone = false;
      if (src) src.close();
      src = null;
      finder = makeBeatFinder();
      gap = 0; lastHit = 0; latest = null;
      if (status === "music" || status === "blocked") { status = "waiting"; publish(); }
      const got = listenTo(media);
      if (got && got.unsupported) { gone = true; status = got.why || "unsupported"; publish(); return; }
      src = got;
    };
    const shape = (r, s) => {
      const cfg = cfgRef.current;
      const f = (0.3 + 0.7 * ((cfg.reach || REACH_DEFAULT) / 10)) * (0.3 + 0.7 * s);     // 0.09 .. 1, how far out of the room it has
      const room = cfg.room || { x: Math.max(40, r.w * 0.35), y: Math.max(30, r.h * 0.35) };
      r.s = s;
      r.dx = room.x * f; r.dy = room.y * f;
      r.dur = 480 + 420 * f;
      r.a0 = Math.min(1, 0.62 + 0.38 * s) * (0.6 + 0.4 * ((cfg.glow || 75) / 100));
    };
    const blast = (now, s) => {
      const r = rings.find((x) => !x.live) || rings.reduce((a, b) => (a.t0 < b.t0 ? a : b), rings[0]);
      if (!r) return;
      const wrap = el.parentNode;
      r.w = (wrap && wrap.offsetWidth) || 1; r.h = (wrap && wrap.offsetHeight) || 1;
      r.live = true; r.t0 = now;
      const looks = cfgRef.current.looks;
      if (looks && looks.length) {
        const look = looks[turn % looks.length];
        turn += 1;
        r.node.style.setProperty("--shk", look.glow);
        if (r.node.lastElementChild) r.node.lastElementChild.style.boxShadow = look.edge;
      }
      shape(r, s);
      r.node.style.visibility = "visible";
      latest = r;
    };
    const drawRings = (now) => {
      rings.forEach((r) => {
        if (!r.live) return;
        const u = (now - r.t0) / r.dur;
        if (u >= 1) { r.live = false; r.node.style.visibility = "hidden"; r.node.style.opacity = "0"; return; }
        const e = 1 - (1 - u) ** 2.6;                                    // a quick burst that slows as it goes
        const sx = 1 + (2 * r.dx * e) / (r.w + 2 * SHOCK_PAD);
        const sy = 1 + (2 * r.dy * e) / (r.h + 2 * SHOCK_PAD);
        r.node.style.transform = `scale(${sx.toFixed(4)}, ${sy.toFixed(4)})`;
        r.node.style.opacity = (r.a0 * Math.min(1, u / 0.06) * (1 - u) ** 1.2).toFixed(3);
      });
    };
    // a new song: let go of the old copy of the sound and listen again once the new song is really playing
    // (listening the moment the file is found can give a copy that never carries any sound)
    const onNewSound = () => {
      if (!media) return;
      if (src) src.close();
      src = null; gone = false; finder = makeBeatFinder();
      gap = 0; lastHit = 0; latest = null;
      if (status === "music" || status === "blocked") { status = "waiting"; publish(); }
      lastTry = performance.now();
    };
    const playing = () => !!media && !media.paused && media.readyState >= 3 && media.currentTime > 0.05;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      const dt = lastT ? Math.min(100, now - lastT) : 16;
      lastT = now;
      let hit = 0;
      if (media && !gone) {
        if (!src) { if (now - lastTry > 500 && playing()) attach(now); }
        else {
          if (finder.step(src.an, src.buf, now)) hit = finder.strength();
          if (status !== "music" && finder.heard()) { status = "music"; publish(); }
          else if (status === "waiting" && now - attachedAt > 4000 && !media.paused && !media.muted && media.volume > 0 && media.currentTime > 3) { status = "blocked"; publish(); }
        }
      }
      if (status !== "music" && demo) hit = fake.step(now);          // the made-up beat, while the real music can not be heard
      if (status !== "music" && !demo) { el.style.transform = ""; drawRings(now); return; }
      if (hit) {
        hold = 70;
        peakEnv = 0.35 + 0.65 * hit;
        if (lastHit && now - lastHit < 1500) gap = gap ? (gap + (now - lastHit)) / 2 : now - lastHit;
        lastHit = now;
        blast(now, hit);
      } else if (latest && status === "music" && now - lastHit < BEAT_PEAK_MS) {
        const s2 = finder.strength();                                // the kick is still building: let the blast grow with it
        if (s2 > latest.s) { shape(latest, s2); peakEnv = 0.35 + 0.65 * s2; }
      }
      const fade = gap ? Math.max(100, Math.min(260, gap * 0.4)) : 240;
      if (hold > 0) { env += (peakEnv - env) * Math.min(1, dt / 45); hold -= dt; } else env *= Math.exp(-dt / fade);
      el.style.opacity = (0.3 + 0.7 * env).toFixed(3);
      // the push: the glow swells outward a little with each beat, like a speaker cone
      const cfg = cfgRef.current;
      const wrap = el.parentNode;
      const w = (wrap && wrap.offsetWidth) || 1;
      const h = (wrap && wrap.offsetHeight) || 1;
      const push = env * (4 + 10 * ((cfg.reach || REACH_DEFAULT) / 10));
      el.style.transform = push > 0.05 ? `scale(${(1 + (2 * push) / w).toFixed(4)}, ${(1 + (2 * push) / h).toFixed(4)})` : "";
      drawRings(now);
    };
    if (media) media.addEventListener("loadedmetadata", onNewSound);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (media) media.removeEventListener("loadedmetadata", onNewSound);
      if (src) src.close();
      el.style.opacity = ""; el.style.transform = "";
      rings.forEach((r) => { r.node.style.visibility = "hidden"; r.node.style.opacity = "0"; r.node.style.transform = ""; });
    };
  }, [enabled, mediaRef, setRef, shocksRef, running, cfgRef, demo]);
  return info;
}

// The blast's colors: only the colors picked, exactly as picked (not lightened, not blended). With two or three colors
// every blast is a set of clean stripes, one pure stripe per color, and the blasts take turns leading: the soft glow
// around the blast and its outer stripe are the leading color. `looks[k]` is the blast that leads with color k;
// `edge` is a box-shadow stack that draws the stripes (the first listed is the outer one).
// With a fourth color picked ("Color between the waves") it fills the glow around every blast, which is wider so it
// fills the space between one blast and the next, and it fills the thin gap between the stripes of a blast. Its
// opacity bar (10-100%) sets how strong that fill is, so a member can set the contrast.
const SHOCK_BAND = 24;     // how thick the whole set of stripes is, in px (the picked colors share it)
function shockLook(sch) {
  const n = sch.colors;
  const rgbs = [0, 1, 2].slice(0, n).map((i) => schemeColor(sch, i));
  const css = rgbs.map(rgbCss);
  const op = sch.blastOp / 100;
  const fill = sch.blast === null ? null : hueColor(sch.blast);
  const dim = fill ? fill.map((v) => Math.round(v * op)) : null;      // the gap between stripes: the color toward the dark background, so it never mixes with a stripe
  const per = SHOCK_BAND / n;
  const looks = rgbs.map((_, k) => {
    const parts = [];
    for (let j = 0; j < n; j += 1) {
      const end = per * (j + 1);
      if (fill) { parts.push([Math.round(end - per / 3), css[(k + j) % n]]); parts.push([Math.round(end), rgbCss(dim)]); }
      else parts.push([Math.round(end), css[(k + j) % n]]);
    }
    return { edge: parts.map(([w, c]) => `inset 0 0 0 ${w}px ${c}`).join(", "), glow: `rgba(${(fill || rgbs[k]).join(",")}, ${fill ? op : 0.85})` };
  });
  return { looks, edge: looks[0].edge, glow: looks[0].glow, blur: fill ? 38 : 26, spread: fill ? 12 : 6 };
}

// ===========================================================================================
// Effects ("Fire and Ice"): frost spikes, snowflakes, flames, embers, mist, bats and ghosts that come out from behind the screen.
// A light scheme can carry one (scheme.fx, see FX_LIST). The Backlight draws it on a canvas that reaches out past the
// frame (as far as the blast room), behind the video. It follows the same beat as the glow (it reads the glow's
// brightness while Beat is on), the Glow slider makes it stronger or softer, a paused video freezes it, and a phone
// set to "reduce motion" gets one still picture. Nothing moves faster than a gentle swell (no strobing).
// The drawing works in a made-up size (about 300 px wide for the picture) and is scaled to the real picture.
// ===========================================================================================
const FX_TAU = Math.PI * 2;
const fxRand = (a, b) => a + Math.random() * (b - a);
const fxClamp = (v, a, b) => Math.max(a, Math.min(b, v));

// A bat seen from the front, wings up and down by `flap` (-1 to 1); drawn on a grid where the wings reach -1 to 1.
function fxBatPath(g, flap) {
  const u = -0.6 * flap;
  g.beginPath();
  g.moveTo(0, -0.22); g.lineTo(0.09, -0.34); g.lineTo(0.15, -0.2);
  g.quadraticCurveTo(0.55, -0.45 + u * 0.6, 1.05, -0.1 + u);
  g.quadraticCurveTo(0.9, 0.08 + u * 0.6, 0.72, 0.1 + u * 0.3);
  g.quadraticCurveTo(0.6, 0.2 + u * 0.3, 0.42, 0.14);
  g.quadraticCurveTo(0.28, 0.26, 0, 0.42);
  g.quadraticCurveTo(-0.28, 0.26, -0.42, 0.14);
  g.quadraticCurveTo(-0.6, 0.2 + u * 0.3, -0.72, 0.1 + u * 0.3);
  g.quadraticCurveTo(-0.9, 0.08 + u * 0.6, -1.05, -0.1 + u);
  g.quadraticCurveTo(-0.55, -0.45 + u * 0.6, -0.15, -0.2);
  g.lineTo(-0.09, -0.34);
  g.closePath();
}

// The little pictures the effects stamp over and over (drawn once, the first time an effect is shown).
let fxAssetsCache = null;
function fxAssets() {
  if (fxAssetsCache) return fxAssetsCache;
  const cache = new Map();
  const canvas = (w, h) => { const c = document.createElement("canvas"); c.width = w; c.height = h; return c; };
  const glowSprite = (rgb, hard) => {
    const key = rgb.join(",") + (hard ? "h" : "");
    if (cache.has(key)) return cache.get(key);
    const c = canvas(64, 64), x = c.getContext("2d"), rgba = (a) => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;
    const gr = x.createRadialGradient(32, 32, 0, 32, 32, 32);
    gr.addColorStop(0, rgba(1));
    if (hard) gr.addColorStop(0.14, rgba(1));
    gr.addColorStop(hard ? 0.34 : 0.3, rgba(hard ? 0.4 : 0.55));
    gr.addColorStop(1, rgba(0));
    x.fillStyle = gr; x.fillRect(0, 0, 64, 64);
    cache.set(key, c);
    return c;
  };
  const tongueSprite = (rgb) => {
    const key = `t${rgb.join(",")}`;
    if (cache.has(key)) return cache.get(key);
    const c = canvas(64, 120), x = c.getContext("2d"), rgba = (a) => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;
    x.shadowColor = rgba(0.9); x.shadowBlur = 8;
    const gr = x.createLinearGradient(0, 108, 0, 12);
    gr.addColorStop(0, rgba(0.95)); gr.addColorStop(0.55, rgba(0.8)); gr.addColorStop(1, rgba(0));
    x.fillStyle = gr;
    x.beginPath(); x.moveTo(32, 108);
    x.bezierCurveTo(6, 100, 8, 64, 25, 42);
    x.bezierCurveTo(30, 32, 29, 22, 35, 12);
    x.bezierCurveTo(39, 26, 38, 34, 42, 44);
    x.bezierCurveTo(58, 64, 58, 100, 32, 108);
    x.closePath(); x.fill();
    cache.set(key, c);
    return c;
  };
  const ramp = (stops, kind) => Array.from({ length: 8 }, (_, i) => {
    const f = (i / 7) * (stops.length - 1), a = Math.floor(f), b = Math.min(stops.length - 1, a + 1), t = f - a;
    const rgb = stops[a].map((v, j) => Math.round(v + (stops[b][j] - v) * t));
    return kind === "tongue" ? tongueSprite(rgb) : glowSprite(rgb, kind === "hard");
  });
  const FIRE = [[255, 248, 205], [255, 192, 72], [255, 112, 26], [212, 46, 10], [112, 20, 12]];
  const BLUE = [[240, 253, 255], [162, 233, 255], [72, 176, 255], [36, 96, 226], [20, 46, 132]];
  const flake = (variant) => {
    const c = canvas(64, 64), x = c.getContext("2d");
    x.translate(32, 32); x.lineCap = "round"; x.strokeStyle = "#eafaff";
    x.shadowColor = "rgba(120,215,255,0.95)"; x.shadowBlur = 6; x.lineWidth = variant === 1 ? 3 : 2.4;
    const branches = variant === 0 ? [[-14, 9]] : variant === 1 ? [[-10, 8], [-19, 10]] : [[-18, 7]];
    for (let i = 0; i < 6; i += 1) {
      x.save(); x.rotate((i * FX_TAU) / 6);
      x.beginPath(); x.moveTo(0, 0); x.lineTo(0, -26);
      branches.forEach(([y, l]) => { x.moveTo(0, y); x.lineTo(l * 0.9, y - l * 0.7); x.moveTo(0, y); x.lineTo(-l * 0.9, y - l * 0.7); });
      x.stroke(); x.restore();
    }
    return c;
  };
  const bats = [-1, -0.5, 0, 0.5, 1].map((f) => {
    const c = canvas(56, 40), x = c.getContext("2d");
    x.translate(28, 24); x.scale(24, 24); fxBatPath(x, f);
    x.fillStyle = "#53294a"; x.fill();
    x.lineWidth = 0.11; x.strokeStyle = "rgba(255,168,88,0.95)"; x.stroke();      // a warm rim and a dusky fill, so the bats show against the dark page
    return c;
  });
  // a little ghost (a sheet with a rounded top and a wavy bottom), two faces
  const ghost = (face) => {
    const c = canvas(64, 80), x = c.getContext("2d");
    const w = 46, h = 60, left = 9, top = 9, r = w / 2;
    x.shadowColor = "rgba(190,210,255,0.95)"; x.shadowBlur = 9;
    const gr = x.createLinearGradient(0, top, 0, top + h);
    gr.addColorStop(0, "rgba(255,255,255,0.97)"); gr.addColorStop(1, "rgba(205,215,255,0.8)");
    x.fillStyle = gr;
    x.beginPath(); x.moveTo(left, top + r); x.arc(left + r, top + r, r, Math.PI, 0);
    x.lineTo(left + w, top + h * 0.84);
    for (let i = 0; i < 3; i += 1) { const x1 = left + w - (w / 3) * i; const x2 = left + w - (w / 3) * (i + 1); x.quadraticCurveTo((x1 + x2) / 2, top + h * 1.12, x2, top + h * 0.84); }
    x.closePath(); x.fill();
    x.shadowBlur = 0; x.fillStyle = "#1d1830";
    [0.3, 0.7].forEach((fx) => { x.beginPath(); x.ellipse(left + w * fx, top + h * 0.36, 3.4, 5, 0, 0, FX_TAU); x.fill(); });
    if (face === 0) { x.beginPath(); x.ellipse(left + w * 0.5, top + h * 0.58, 4.2, 5.6, 0, 0, FX_TAU); x.fill(); }
    else { x.lineWidth = 2.4; x.lineCap = "round"; x.strokeStyle = "#1d1830"; x.beginPath(); x.arc(left + w * 0.5, top + h * 0.5, 7, 0.25 * Math.PI, 0.75 * Math.PI); x.stroke(); }
    return c;
  };
  fxAssetsCache = {
    GHOSTS: [ghost(0), ghost(1)],
    FIRE_RAMP: ramp(FIRE, "tongue"), BLUE_RAMP: ramp(BLUE, "tongue"),
    FIRE_SPARK: ramp(FIRE, "hard"), BLUE_SPARK: ramp(BLUE, "hard"),
    ORANGE_SPARK: ramp([[255, 178, 70], [255, 120, 34], [210, 56, 22]], "hard"),
    FLAKES: [flake(0), flake(1), flake(2)], STAR_GLOW: glowSprite([190, 235, 255]), BATS: bats,
    mist: (rgb) => [glowSprite(rgb)],
  };
  return fxAssetsCache;
}

// Where on the picture's edge a particle starts. `w` = how much of the top, right, bottom and left edge to use;
// `region` "left" or "right" keeps it to one half. nx, ny point straight out from the edge.
function fxSidePick(rect, w, region, cx) {
  const lens = [rect.w * w[0], rect.h * w[1], rect.w * w[2], rect.h * w[3]];
  const total = lens[0] + lens[1] + lens[2] + lens[3];
  for (let tries = 0; tries < 14; tries += 1) {
    let r = Math.random() * total;
    let side = 0;
    while (side < 3 && r > lens[side]) { r -= lens[side]; side += 1; }
    const f = Math.random();
    let x; let y; let nx = 0; let ny = 0;
    if (side === 0) { x = rect.x + f * rect.w; y = rect.y; ny = -1; }
    else if (side === 1) { x = rect.x + rect.w; y = rect.y + f * rect.h; nx = 1; }
    else if (side === 2) { x = rect.x + f * rect.w; y = rect.y + rect.h; ny = 1; }
    else { x = rect.x; y = rect.y + f * rect.h; nx = -1; }
    if (region === "left" && x >= cx) continue;
    if (region === "right" && x < cx) continue;
    return { x, y, nx, ny };
  }
  return null;
}

// A bunch of particles that are born at a steady rate, move, and die. `spawn(env, boost)` makes one (boost 2 = a beat burst).
function fxPool(cfg, spawn) {
  const parts = [];
  let acc = 0;
  return {
    parts,
    tick(env, dt, step) {
      acc += cfg.rate * env.k * dt;
      while (acc >= 1) { acc -= 1; if (parts.length < cfg.max) { const p = spawn(env, 1); if (p) parts.push(p); } }
      for (let i = parts.length - 1; i >= 0; i -= 1) {
        const p = parts[i];
        p.t += dt / p.life;
        if (p.t >= 1) { parts[i] = parts[parts.length - 1]; parts.pop(); } else step(p, env, dt);
      }
    },
    burst(env, n) { for (let i = 0; i < n * env.k && parts.length < cfg.max + 60; i += 1) { const p = spawn(env, 2); if (p) parts.push(p); } },
  };
}

function FxFlames(o) {
  const A = fxAssets();
  const cfg = { ramp: A.FIRE_RAMP, rate: 85, w: [1, 0.75, 0.1, 0.75], region: "all", size: [22, 38], rise: [30, 66], out: 30, life: [0.6, 1.1], alpha: 0.8, max: 280, ...o };
  const P = fxPool(cfg, (env, boost) => {
    const p = fxSidePick(env.rect, cfg.w, cfg.region, env.cx);
    if (!p) return null;
    const sp = boost > 1 ? 1.7 : 1;
    return { x: p.x - p.nx * 6, y: p.y - p.ny * 6, vx: p.nx * cfg.out * fxRand(0.4, 1.1) * sp + fxRand(-10, 10), vy: p.ny * cfg.out * 0.4 * sp - fxRand(cfg.rise[0], cfg.rise[1]) * (sp > 1 ? 1.25 : 1), t: 0, life: fxRand(cfg.life[0], cfg.life[1]), size: fxRand(cfg.size[0], cfg.size[1]) * env.size, seed: Math.random() * 100 };
  });
  return {
    update(env, dt) { P.tick(env, dt, (p, e, d) => { p.x += (p.vx + Math.sin(e.time * 3.1 + p.seed) * 16) * d; p.y += p.vy * d; p.vy *= 1 - 0.4 * d; }); },
    burst: P.burst,
    draw(g, env) {
      g.globalCompositeOperation = "lighter";
      const n = cfg.ramp.length;
      const d = env.d;
      P.parts.forEach((p) => {
        const t = p.t;
        const a = (t < 0.15 ? t / 0.15 : (1 - (t - 0.15) / 0.85) ** 1.2) * cfg.alpha;
        const s = p.size * (1 - 0.5 * t) * (1 + 0.3 * env.beat);
        const ang = Math.sin(env.time * 3.4 + p.seed) * 0.2 + p.vx * 0.004;
        const c = Math.cos(ang);
        const sn = Math.sin(ang);
        const w = s * 0.85 * (1 + 0.12 * Math.sin(env.time * 7 + p.seed * 3));
        const h = s * 1.7;
        g.globalAlpha = a;
        g.setTransform(d * c, d * sn, -d * sn, d * c, d * p.x, d * p.y);
        g.drawImage(cfg.ramp[Math.min(n - 1, Math.floor(t * n))], -w / 2, -h, w, h);
      });
      g.setTransform(d, 0, 0, d, 0, 0); g.globalAlpha = 1; g.globalCompositeOperation = "source-over";
    },
  };
}

function FxEmbers(o) {
  const A = fxAssets();
  const cfg = { ramp: A.FIRE_SPARK, rate: 14, w: [1, 1, 0.6, 1], region: "all", size: [1.6, 3.6], life: [1.4, 3], rise: [25, 80], out: [30, 90], alpha: 0.95, max: 180, ...o };
  const P = fxPool(cfg, (env, boost) => {
    const p = fxSidePick(env.rect, cfg.w, cfg.region, env.cx);
    if (!p) return null;
    const sp = boost > 1 ? 1.8 : 1;
    const out = fxRand(cfg.out[0], cfg.out[1]) * sp;
    return { x: p.x - p.nx * 6, y: p.y - p.ny * 6, vx: p.nx * out + fxRand(-20, 20), vy: p.ny * out * 0.6 - fxRand(cfg.rise[0], cfg.rise[1]) * (boost > 1 ? 1.3 : 1), t: 0, life: fxRand(cfg.life[0], cfg.life[1]), size: fxRand(cfg.size[0], cfg.size[1]) * env.size, seed: Math.random() * 100 };
  });
  return {
    update(env, dt) { P.tick(env, dt, (p, e, d) => { p.x += (p.vx + Math.sin(e.time * 1.7 + p.seed) * 14) * d; p.y += p.vy * d; p.vx *= 1 - 0.3 * d; p.vy *= 1 - 0.12 * d; }); },
    burst: P.burst,
    draw(g, env) {
      g.globalCompositeOperation = "lighter";
      const n = cfg.ramp.length;
      P.parts.forEach((p) => {
        const t = p.t;
        g.globalAlpha = Math.min(1, t * 8) * (1 - t) * (0.78 + 0.22 * Math.sin(env.time * 5 + p.seed)) * cfg.alpha;
        const s = p.size * 6;
        g.drawImage(cfg.ramp[Math.min(n - 1, Math.floor(t * n * 0.8))], p.x - s / 2, p.y - s / 2, s, s);
      });
      g.globalAlpha = 1; g.globalCompositeOperation = "source-over";
    },
  };
}

function FxSnow(o) {
  const A = fxAssets();
  const cfg = { rate: 15, w: [1, 1, 0.8, 1], region: "all", size: [5, 11], life: [2.4, 4.4], out: [24, 70], max: 120, ...o };
  const P = fxPool(cfg, (env, boost) => {
    const p = fxSidePick(env.rect, cfg.w, cfg.region, env.cx);
    if (!p) return null;
    const sp = boost > 1 ? 1.8 : 1;
    const out = fxRand(cfg.out[0], cfg.out[1]) * sp;
    return { x: p.x - p.nx * 6, y: p.y - p.ny * 6, vx: p.nx * out + fxRand(-15, 15), vy: p.ny * out + fxRand(-8, 12), t: 0, life: fxRand(cfg.life[0], cfg.life[1]), size: fxRand(cfg.size[0], cfg.size[1]) * env.size, rot: fxRand(0, FX_TAU), vr: fxRand(-0.9, 0.9), seed: Math.random() * 100, v: Math.floor(Math.random() * 3) };
  });
  return {
    update(env, dt) { P.tick(env, dt, (p, e, d) => { p.rot += p.vr * d; p.x += (p.vx + Math.sin(e.time * 1.3 + p.seed) * 10) * d; p.y += p.vy * d; p.vy += 9 * d; p.vx *= 1 - 0.25 * d; }); },
    burst: P.burst,
    draw(g, env) {
      const d = env.d;
      P.parts.forEach((p) => {
        const c = Math.cos(p.rot);
        const s = Math.sin(p.rot);
        g.globalAlpha = Math.min(1, p.t * 7) * (1 - p.t) ** 0.7 * 0.95;
        g.setTransform(d * c, d * s, -d * s, d * c, d * p.x, d * p.y);
        g.drawImage(A.FLAKES[p.v], -p.size / 2, -p.size / 2, p.size, p.size);
      });
      g.setTransform(d, 0, 0, d, 0, 0); g.globalAlpha = 1;
    },
  };
}

function FxMist(o) {
  const cfg = { sprite: fxAssets().mist([170, 230, 255]), rate: 4, w: [1, 1, 0.8, 1], region: "all", spot: null, size: [90, 150], life: [2.6, 4.6], alpha: 0.14, max: 40, ...o };
  const P = fxPool(cfg, (env) => {
    const p = cfg.spot === "top-center" ? { x: env.cx + fxRand(-34, 34), y: env.rect.y, nx: 0, ny: -1 } : fxSidePick(env.rect, cfg.w, cfg.region, env.cx);
    if (!p) return null;
    return { x: p.x - p.nx * 10, y: p.y - p.ny * 10, vx: p.nx * fxRand(8, 26) + fxRand(-6, 6), vy: p.ny * fxRand(6, 18) - fxRand(4, 16), t: 0, life: fxRand(cfg.life[0], cfg.life[1]), size: fxRand(cfg.size[0], cfg.size[1]) * env.size };
  });
  return {
    update(env, dt) { P.tick(env, dt, (p, e, d) => { p.x += p.vx * d; p.y += p.vy * d; }); },
    burst(env, n) { P.burst(env, n * 0.15); },
    draw(g, env) {
      g.globalCompositeOperation = "lighter";
      P.parts.forEach((p) => {
        g.globalAlpha = cfg.alpha * Math.sin(Math.PI * p.t) * (1 + 0.4 * env.beat);
        const s = p.size * (0.8 + 0.7 * p.t);
        g.drawImage(cfg.sprite[0], p.x - s / 2, p.y - s / 2, s, s);
      });
      g.globalAlpha = 1; g.globalCompositeOperation = "source-over";
    },
  };
}

// Frost spikes growing out along the frame, slowly longer and shorter, with a few twinkles.
function FxSpikes(o) {
  const A = fxAssets();
  const cfg = { region: "all", gap: 15, len: [22, 60], w: [2.5, 5.5], ...o };
  let sp = [];
  const tri = (g, s, len, w, lean) => {
    const tx = -s.ny; const ty = s.nx; const bx = s.x - s.nx * 3; const by = s.y - s.ny * 3;
    g.moveTo(bx - tx * w, by - ty * w);
    g.lineTo(bx + s.nx * len + tx * lean, by + s.ny * len + ty * lean);
    g.lineTo(bx + tx * w, by + ty * w);
  };
  return {
    layout(env) {
      sp = [];
      const r = env.rect;
      const add = (x, y, nx, ny) => {
        if (cfg.region === "left" && x >= env.cx) return;
        if (cfg.region === "right" && x < env.cx) return;
        sp.push({ x, y, nx, ny, base: fxRand(cfg.len[0], cfg.len[1]), ph: fxRand(0, FX_TAU), w: fxRand(cfg.w[0], cfg.w[1]), lean: fxRand(-0.35, 0.35), tw: Math.random() < 0.3 });
      };
      for (let x = r.x + 8; x < r.x + r.w - 4; x += cfg.gap * fxRand(0.8, 1.2)) { add(x, r.y, 0, -1); add(x, r.y + r.h, 0, 1); }
      for (let y = r.y + 8; y < r.y + r.h - 4; y += cfg.gap * fxRand(0.8, 1.2)) { add(r.x, y, -1, 0); add(r.x + r.w, y, 1, 0); }
    },
    update() {},
    burst() {},
    draw(g, env) {
      const t = env.time;
      const lens = sp.map((s) => s.base * (0.55 + 0.45 * Math.sin(t * 0.7 + s.ph)) * env.size * (1 + env.beat));
      g.globalCompositeOperation = "lighter";
      g.fillStyle = "rgba(90,190,255,0.10)"; g.beginPath();
      sp.forEach((s, i) => tri(g, s, lens[i] * 1.12, s.w * 2.6, s.lean * lens[i] * 0.5)); g.fill();
      g.globalCompositeOperation = "source-over";
      g.fillStyle = "rgba(150,226,255,0.6)"; g.beginPath();
      sp.forEach((s, i) => tri(g, s, lens[i], s.w, s.lean * lens[i] * 0.5)); g.fill();
      g.fillStyle = "rgba(245,253,255,0.92)"; g.beginPath();
      sp.forEach((s, i) => tri(g, s, lens[i] * 0.92, s.w * 0.35, s.lean * lens[i] * 0.46)); g.fill();
      sp.forEach((s, i) => {
        if (!s.tw) return;
        const a = Math.max(0, Math.sin(t * 1.5 + s.ph * 3)) ** 8;
        if (a < 0.03) return;
        const x = s.x + s.nx * lens[i] - s.ny * s.lean * lens[i] * 0.5;
        const y = s.y + s.ny * lens[i] + s.nx * s.lean * lens[i] * 0.5;
        const r = 3 + 5 * a;
        const q = r * 0.16;
        g.globalAlpha = a * 0.8; g.drawImage(A.STAR_GLOW, x - r * 2, y - r * 2, r * 4, r * 4);
        g.globalAlpha = a; g.beginPath();
        g.moveTo(x, y - r); g.lineTo(x + q, y - q); g.lineTo(x + r, y); g.lineTo(x + q, y + q); g.lineTo(x, y + r); g.lineTo(x - q, y + q); g.lineTo(x - r, y); g.lineTo(x - q, y - q); g.closePath();
        g.fillStyle = "#fff"; g.fill();
      });
      g.globalAlpha = 1;
    },
  };
}

// A few big bats flapping out from behind the screen (Haunted Night).
function FxBats() {
  const parts = [];
  let timer = fxRand(0.1, 0.5);
  const most = (env) => (env.level === 0 ? 2 : env.level === 1 ? 3 : 4);
  const spawn = (env) => {
    const p = fxSidePick(env.rect, [1, 0.8, 0, 0.8], "all", env.cx);
    if (!p) return;
    const sp = fxRand(55, 95);
    parts.push({ x: p.x, y: p.y, vx: p.nx * sp * fxRand(0.5, 1) + (p.nx === 0 ? fxRand(-40, 40) : 0), vy: -fxRand(25, 65), t: 0, life: fxRand(4, 6.5), s: fxRand(14, 23) * env.size, seed: Math.random() * 100 });
  };
  return {
    update(env, dt) {
      timer -= dt;
      if (timer <= 0) { timer = fxRand(1.8, 3.8) / env.k; if (parts.length < most(env)) spawn(env); }
      for (let i = parts.length - 1; i >= 0; i -= 1) {
        const p = parts[i];
        p.t += dt / p.life;
        if (p.t >= 1) parts.splice(i, 1);
        else { p.x += (p.vx + Math.sin(env.time * 1.1 + p.seed) * 18) * dt; p.y += (p.vy + Math.sin(env.time * 1.7 + p.seed * 2) * 22) * dt; }
      }
    },
    burst(env, n) { if (n > 12 && parts.length < most(env) && Math.random() < 0.55) spawn(env); },
    draw(g, env) {
      parts.forEach((p) => {
        g.save(); g.translate(p.x, p.y); g.rotate(fxClamp(p.vx * 0.004, -0.3, 0.3)); g.scale(p.s, p.s);
        g.globalAlpha = Math.min(1, p.t * 6, (1 - p.t) * 4);
        fxBatPath(g, Math.sin(env.time * 11 + p.seed));
        g.fillStyle = "#0c0714"; g.fill();
        g.lineWidth = 1.3 / p.s; g.strokeStyle = "rgba(255,150,70,0.8)"; g.stroke();
        g.restore();
      });
      g.globalAlpha = 1;
    },
  };
}

// A river of small bats, like the evening flight from the Congress Avenue Bridge in Austin.
function FxBatSwarm() {
  const A = fxAssets();
  const cfg = { rate: 14, max: 170, size: [5, 10] };
  const P = fxPool(cfg, (env, boost) => {
    const p = fxSidePick(env.rect, [1, 0.3, 0, 0.3], "all", env.cx);
    if (!p) return null;
    const sp = boost > 1 ? 1.5 : 1;
    return { x: p.x - p.nx * 4, y: p.y - p.ny * 4, vx: fxRand(15, 50) * sp + p.nx * 25, vy: -fxRand(35, 85) * sp, t: 0, life: fxRand(3, 5.2), size: fxRand(cfg.size[0], cfg.size[1]) * env.size, seed: Math.random() * 100 };
  });
  return {
    update(env, dt) { P.tick(env, dt, (p, e, d) => { p.x += (p.vx + Math.sin(e.time * 1.6 + p.seed) * 22) * d; p.y += (p.vy + Math.sin(e.time * 2.3 + p.seed * 1.7) * 18) * d; p.vx += 12 * d; }); },
    burst(env, n) { P.burst(env, n * 0.7); },
    draw(g, env) {
      const d = env.d;
      P.parts.forEach((p) => {
        const w = p.size * 2.2;
        const h = (w * 40) / 56;
        const ang = fxClamp(p.vx * 0.006, -0.35, 0.35);
        const c = Math.cos(ang);
        const sn = Math.sin(ang);
        const idx = Math.round(((Math.sin(env.time * 14 + p.seed) + 1) / 2) * 4);
        g.globalAlpha = Math.min(1, p.t * 8, (1 - p.t) * 3);
        g.setTransform(d * c, d * sn, -d * sn, d * c, d * p.x, d * p.y);
        g.drawImage(A.BATS[idx], -w / 2, -h * 0.6, w, h);
      });
      g.setTransform(d, 0, 0, d, 0, 0); g.globalAlpha = 1;
    },
  };
}

// A few friendly ghosts that float up from behind the screen, bobbing, and hop a little on each beat (Friendly Ghosts).
function FxGhosts() {
  const A = fxAssets();
  const cfg = { rate: 1.6, max: 9 };
  const P = fxPool(cfg, (env) => {
    const p = fxSidePick(env.rect, [1, 0.8, 0.2, 0.8], "all", env.cx);
    if (!p) return null;
    return { x: p.x - p.nx * 8, y: p.y - p.ny * 8, vx: p.nx * fxRand(10, 30) + fxRand(-8, 8), vy: -fxRand(14, 34) + p.ny * 8, t: 0, life: fxRand(5, 8), size: fxRand(24, 36) * env.size, seed: Math.random() * 100, face: Math.random() < 0.5 ? 0 : 1, hop: 0 };
  });
  return {
    update(env, dt) {
      P.tick(env, dt, (p, e, d) => {
        p.x += (p.vx + Math.sin(e.time * 1.3 + p.seed) * 12) * d;
        p.y += (p.vy + Math.sin(e.time * 2 + p.seed * 1.9) * 10 - p.hop * 50) * d;
        p.hop = Math.max(0, p.hop - 2.5 * d);
      });
    },
    burst(env, n) { if (n > 12) P.parts.forEach((p) => { p.hop = 1; }); },
    draw(g, env) {
      const d = env.d;
      P.parts.forEach((p) => {
        const t = p.t;
        const fade = Math.min(1, t * 8, (1 - t) * 4);
        const ang = Math.sin(env.time * 1.3 + p.seed) * 0.12 + p.vx * 0.003;
        const c = Math.cos(ang);
        const s = Math.sin(ang);
        const w = p.size * (1 + 0.04 * Math.sin(env.time * 3 + p.seed));
        const h = p.size * 1.25;
        g.setTransform(d, 0, 0, d, 0, 0);
        g.globalCompositeOperation = "lighter"; g.globalAlpha = 0.28 * fade;
        g.drawImage(A.mist([190, 205, 255])[0], p.x - p.size * 1.3, p.y - p.size * 1.3, p.size * 2.6, p.size * 2.6);
        g.globalCompositeOperation = "source-over"; g.globalAlpha = 0.88 * fade;
        g.setTransform(d * c, d * s, -d * s, d * c, d * p.x, d * p.y);
        g.drawImage(A.GHOSTS[p.face], -w / 2, -h / 2, w, h);
      });
      g.setTransform(d, 0, 0, d, 0, 0); g.globalAlpha = 1; g.globalCompositeOperation = "source-over";
    },
  };
}

// What each effect is made of (the order they are drawn in).
const FX_BUILD = {
  frost: () => [FxMist({ rate: 3.5, alpha: 0.13 }), FxSnow({ rate: 15 }), FxSpikes({})],
  flames: () => [FxFlames({}), FxEmbers({ rate: 14 })],
  fireice: () => {
    const A = fxAssets();
    return [
      FxMist({ sprite: A.mist([170, 230, 255]), rate: 2.5, alpha: 0.12, region: "left" }),
      FxMist({ sprite: A.mist([205, 190, 255]), rate: 2.5, alpha: 0.075, spot: "top-center", size: [90, 130] }),
      FxSnow({ rate: 11, region: "left" }), FxSpikes({ region: "left" }),
      FxFlames({ rate: 60, region: "right" }), FxEmbers({ rate: 9, region: "right" }),
    ];
  },
  blueflame: () => { const A = fxAssets(); return [FxFlames({ ramp: A.BLUE_RAMP }), FxEmbers({ ramp: A.BLUE_SPARK, rate: 12 }), FxSnow({ rate: 4, size: [4, 8] })]; },
  ember: () => { const A = fxAssets(); return [FxMist({ sprite: A.mist([255, 140, 60]), rate: 2, alpha: 0.09 }), FxEmbers({ rate: 9, rise: [18, 50], out: [14, 50], life: [2.6, 4.6], size: [2, 4], w: [0.7, 1, 1, 1] })]; },
  haunted: () => { const A = fxAssets(); return [FxMist({ sprite: A.mist([135, 75, 255]), rate: 5, alpha: 0.16 }), FxEmbers({ ramp: A.ORANGE_SPARK, rate: 12, size: [1.8, 3.4] }), FxBats()]; },
  batflight: () => [FxBatSwarm()],
  ghosts: () => [FxMist({ sprite: fxAssets().mist([150, 120, 255]), rate: 3, alpha: 0.1 }), FxGhosts()],
};

// The same effects inside the caption bar (the steel plate under the picture): icicles and snow come from the top edge,
// fire and embers rise from the bottom edge, everything else drifts in from the bottom. `top` and `bottom` are drawn from
// two thin lines (the bar's top and bottom edges), at a small scale so they fit the bar. They play behind the words.
const BAR_FX = {
  frost: () => ({ top: [FxMist({ rate: 3.5, alpha: 0.13 }), FxSnow({ rate: 15 }), FxSpikes({})], bottom: [] }),
  fireice: () => { const A = fxAssets(); return { top: [FxMist({ sprite: A.mist([170, 230, 255]), rate: 2.5, alpha: 0.12 }), FxSnow({ rate: 11 }), FxSpikes({})], bottom: [FxFlames({ rate: 60 }), FxEmbers({ rate: 9 })] }; },
  flames: () => ({ top: [], bottom: [FxFlames({}), FxEmbers({ rate: 14 })] }),
  blueflame: () => { const A = fxAssets(); return { top: [FxSnow({ rate: 4, size: [4, 8] })], bottom: [FxFlames({ ramp: A.BLUE_RAMP }), FxEmbers({ ramp: A.BLUE_SPARK, rate: 12 })] }; },
  ember: () => ({ top: [], bottom: FX_BUILD.ember() }),
  haunted: () => ({ top: [], bottom: FX_BUILD.haunted() }),
  batflight: () => ({ top: [], bottom: FX_BUILD.batflight() }),
  ghosts: () => ({ top: [], bottom: FX_BUILD.ghosts() }),
};

// The canvas of the caption bar's effect. It fills the bar (the bar clips it), freezes while the song is paused, and is not drawn at all
// on a device set to "reduce motion".
function BarFx({ fx, paused }) {
  const cvRef = useRef(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => {
    const cv = cvRef.current;
    const bar = cv && cv.parentElement;
    const g = cv && cv.getContext("2d");
    if (!g || !bar || !BAR_FX[fx]) return undefined;
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const L = BAR_FX[fx]();
    const mk = () => ({ rect: { x: 0, y: 0, w: 1, h: 0 }, cx: 0, d: 1, sc: 0.55, W: 1, H: 1, k: 1.19, size: 1.04, level: 1, time: 0, beat: 0 });
    const eT = mk();
    const eB = mk();
    let time = fxRand(0, 60);
    let last = 0;
    let raf = 0;
    const measure = () => {
      const cw = bar.clientWidth;
      const ch = bar.clientHeight;
      if (cw < 20 || ch < 8) return false;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = Math.round(cw * dpr); cv.height = Math.round(ch * dpr);
      [[eT, 0], [eB, 1]].forEach(([e, bot]) => { e.d = dpr * e.sc; e.W = cw / e.sc; e.H = ch / e.sc; e.rect = { x: 0, y: bot ? e.H : 0, w: e.W, h: 0 }; e.cx = e.W / 2; });
      L.top.forEach((l) => l.layout && l.layout(eT));
      L.bottom.forEach((l) => l.layout && l.layout(eB));
      return true;
    };
    const draw = () => {
      g.setTransform(eT.d, 0, 0, eT.d, 0, 0); g.clearRect(0, 0, eT.W, eT.H);
      L.top.forEach((l) => l.draw(g, eT));
      g.setTransform(eB.d, 0, 0, eB.d, 0, 0);
      L.bottom.forEach((l) => l.draw(g, eB));
    };
    const step = (dt) => {
      time += dt;
      [eT, eB].forEach((e) => { e.time = time; e.beat = 0.35 + 0.35 * Math.sin(time * 3.1); });
      L.top.forEach((l) => l.update(eT, dt));
      L.bottom.forEach((l) => l.update(eB, dt));
    };
    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (pausedRef.current) { last = 0; return; }
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016;
      last = now;
      step(dt); draw();
    };
    const start = () => {
      cancelAnimationFrame(raf);
      if (!measure()) return;
      for (let i = 0; i < 60; i += 1) step(1 / 30);      // start from a full bar, not an empty one
      draw();
      last = 0;
      raf = requestAnimationFrame(frame);
    };
    start();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(start) : null;
    if (ro) ro.observe(bar);
    return () => { cancelAnimationFrame(raf); if (ro) ro.disconnect(); };
  }, [fx]);
  return <canvas ref={cvRef} aria-hidden="true" data-testid="caption-bar-fx" data-fx={fx} />;
}

// The canvas itself. Lives inside the Backlight (so it sits behind the video) and reaches out past the frame by the blast room.
function FxLayer({ fx, glow, paused, room, bounds, setRef }) {
  const cvRef = useRef(null);
  const glowRef = useRef(glow);
  const pausedRef = useRef(paused);
  glowRef.current = glow;
  pausedRef.current = paused;
  // How far the canvas reaches past the frame on each side. In the big player `bounds` is the free room on each side, so the effects
  // can be seen all the way around the picture, out to the edges of the screen. Elsewhere it is a little way out, evenly.
  const padX = fxClamp(room && room.x ? room.x : 60, 56, 160);
  const padY = fxClamp(room && room.y ? room.y : 40, 56, 160);
  const padL = bounds ? fxClamp(Math.round(bounds.l), 56, 1600) : padX;
  const padR = bounds ? fxClamp(Math.round(bounds.r), 56, 1600) : padX;
  const padT = bounds ? fxClamp(Math.round(bounds.t), 56, 1600) : padY;
  const padB = bounds ? fxClamp(Math.round(bounds.b), 56, 1600) : padY;
  useEffect(() => {
    const cv = cvRef.current;
    const g = cv && cv.getContext("2d");
    if (!g || !FX_BUILD[fx]) return undefined;
    const still = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const layers = FX_BUILD[fx]();
    const env = { rect: { x: 0, y: 0, w: 1, h: 1 }, cx: 0, d: 1, sc: 1, W: 1, H: 1, k: 1, size: 1, level: 1, time: 0, beat: 0 };
    let time = fxRand(0, 60);
    let raf = 0;
    let last = 0;
    let prev = 0;
    let lastBurst = 0;
    // The Glow slider (30-100) sets how strong the effect is.
    const setLevel = () => {
      const k = fxClamp(0.5 + ((glowRef.current - 30) / 70) * 1.2, 0.5, 1.7);
      env.k = k * Math.sqrt(env.sc);
      env.size = 0.9 + ((k - 0.5) / 1.2) * 0.25;
      env.level = k < 0.8 ? 0 : k < 1.3 ? 1 : 2;
    };
    const measure = () => {
      const cw = cv.clientWidth;
      const ch = cv.clientHeight;
      const rw = cw - padL - padR;
      const rh = ch - padT - padB;
      if (rw < 20 || rh < 20) return false;
      const sc = fxClamp(rw / 300, 0.8, 3);
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      cv.width = Math.round(cw * dpr); cv.height = Math.round(ch * dpr);
      env.sc = sc; env.d = dpr * sc; env.W = cw / sc; env.H = ch / sc;
      env.rect = { x: padL / sc, y: padT / sc, w: rw / sc, h: rh / sc };
      env.cx = env.rect.x + env.rect.w / 2;
      layers.forEach((l) => l.layout && l.layout(env));
      setLevel();
      return true;
    };
    const draw = () => { g.setTransform(env.d, 0, 0, env.d, 0, 0); g.clearRect(0, 0, env.W, env.H); layers.forEach((l) => l.draw(g, env)); };
    // Beat: while the beat code drives the glow it writes the brightness on the glow's own style; that swell is the beat.
    const readBeat = () => {
      const el = setRef && setRef.current;
      const o = el ? parseFloat(el.style.opacity) : NaN;
      return Number.isFinite(o) ? fxClamp((o - 0.3) / 0.7, 0, 1) : 0;
    };
    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (pausedRef.current) { last = 0; return; }
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016;
      last = now; time += dt; env.time = time; setLevel();
      const b = readBeat();
      if (b > 0.3 && b - prev > 0.08 && now - lastBurst > 320) { lastBurst = now; layers.forEach((l) => l.burst && l.burst(env, 24)); }
      prev = b;
      env.beat = Math.max(b, env.beat * Math.exp(-dt / 0.3));
      layers.forEach((l) => l.update(env, dt));
      draw();
    };
    const start = () => {
      cancelAnimationFrame(raf);
      if (!measure()) return;
      for (let i = 0; i < 45; i += 1) { time += 1 / 30; env.time = time; layers.forEach((l) => l.update(env, 1 / 30)); }     // get going from a full picture, not an empty one
      draw();
      last = 0;
      if (!still) raf = requestAnimationFrame(frame);
    };
    start();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(start) : null;
    if (ro) ro.observe(cv);
    return () => { cancelAnimationFrame(raf); if (ro) ro.disconnect(); };
  }, [fx, padL, padR, padT, padB, setRef]);
  const fade = "linear-gradient(to bottom, transparent 0, #000 26px)";
  return (
    <canvas ref={cvRef} aria-hidden="true" data-testid="backlight-fx" data-fx={fx}
      style={{ position: "absolute", left: -padL, top: -padT, width: `calc(100% + ${padL + padR}px)`, height: `calc(100% + ${padT + padB}px)`, pointerEvents: "none", WebkitMaskImage: fade, maskImage: fade }} />
  );
}

// The LED strip: fills its parent and glows outward past the edges. Put it in a box the shape of
// the video frame, behind the video. With the Beat speed it needs `mediaRef` (the video) to hear the music.
//   demo   - use a made-up beat whenever the music can not be heard (the sample screens)
//   room   - how far (px) a blast may travel to the sides and up/down, {x, y}; the default is a little over a third of the frame
//   bounds - the free room on each side of the frame, {l, r, t, b} (px): the effects are drawn out to there (the big player passes the whole screen)
//   onInfo - called with {status, mode} (see useBeatGlow) while Beat is on, and with null when it is not
export function Backlight({ scheme, paused = false, mediaRef = null, demo = false, room = null, bounds = null, onInfo = null }) {
  const sch = normScheme(scheme);
  const setRef = useRef(null);
  const shocksRef = useRef(null);
  const cfgRef = useRef({});
  const beat = sch.speed === BEAT && sch.style !== "off" && sch.style !== "steady";
  const shock = beat ? shockLook(sch) : null;
  cfgRef.current = { reach: sch.reach, glow: sch.glow, room, looks: shock ? shock.looks : null };
  const info = useBeatGlow(beat, mediaRef, setRef, shocksRef, !paused, cfgRef, demo);
  const live = beat && (info.mode === "music" || info.mode === "demo");
  useEffect(() => { if (onInfo) onInfo(beat ? { status: info.status, mode: info.mode } : null); }, [onInfo, beat, info.status, info.mode]);
  if (sch.style === "off") return null;
  const g = schemeGradient(sch);
  const f = SPEED_FACTOR[sch.style][sch.speed - 1];
  const vars = {
    "--gl": sch.glow / 100,
    "--per": `${(beat ? BEAT_STEADY_PERIOD : (sch.style === "heartbeat" ? 1.3 : 2.8) / f).toFixed(2)}s`,
    "--spin": `${(6 / f).toFixed(2)}s`,
  };
  const maypole = sch.style.startsWith("maypole");
  // Beat: while the music (or the made-up beat) is driving it nothing but the beat code moves the brightness; until then (or when
  // the sound can not be read) it pulses steadily. A Maypole keeps turning either way.
  let cls = sch.style;
  if (beat) cls = maypole ? `${sch.style}${live ? "" : " pulse"}` : (live ? "beat" : "pulse");
  return (
    <div className={`dtm-bl-wrap${paused ? " is-paused" : ""}`} style={vars} aria-hidden="true" data-testid="backlight" data-style={sch.style}
      data-beat={beat ? (info.mode === "demo" ? "demo" : live ? "music" : "steady") : undefined} data-beat-why={beat ? info.status : undefined}>
      <div ref={setRef} className={`dtm-bl-set ${cls}`}>
        <div className="dtm-bl dtm-bl-glow"><div className="dtm-bl-in" style={{ background: g }} /></div>
        <div className="dtm-bl dtm-bl-ring" style={{ background: g }} />
      </div>
      {sch.fx && <FxLayer fx={sch.fx} glow={sch.glow} paused={paused} room={room} bounds={bounds} setRef={setRef} />}
      {beat && (
        <div ref={shocksRef} className="dtm-bl-set dtm-bl-shocks" style={{ "--shk": shock.glow, "--shk-b": `${shock.blur}px`, "--shk-s": `${shock.spread}px` }} data-testid="backlight-shocks">
          {Array.from({ length: SHOCKS }, (_, i) => (
            <div key={i} className="dtm-bl-shock"><div className="dtm-bl-shk-glow" /><div className="dtm-bl-shk-edge" style={{ boxShadow: shock.edge }} /></div>
          ))}
        </div>
      )}
    </div>
  );
}

// A small dot (or two or three) showing a scheme's colors.
export function SchemeDot({ scheme, size = 14 }) {
  const sch = normScheme(scheme);
  if (sch.style === "off") return <span className="inline-block rounded-full border border-white/25 shrink-0" style={{ width: size, height: size }} title="No lights" />;
  const cs = [0, 1, 2].slice(0, sch.colors).map((i) => rgbCss(schemeColor(sch, i)));
  const bg = cs.length === 1 ? cs[0] : `conic-gradient(${[...cs, cs[0]].join(", ")})`;
  return <span className="inline-block rounded-full shrink-0" style={{ width: size, height: size, background: bg, boxShadow: `0 0 6px ${cs[0]}`, border: "1px solid rgba(255,255,255,0.3)" }} />;
}

// Which lights a song shows: its primary mood (the one marked in Admin, else its first mood).
export function songMoodName(song) {
  const names = (song && song.moods) || [];
  return song && song.primary_mood && names.includes(song.primary_mood) ? song.primary_mood : names[0] || null;
}
export function songLights(song, moodMap) {
  const name = songMoodName(song);
  return name && moodMap && moodMap[name] ? normScheme(moodMap[name]) : null;
}

let moodMapCache = null;
// mood name -> its lights; refreshed when Admin changes a mood (it sends "dtm-moods-changed")
function useMoodLights(enabled) {
  const [map, setMap] = useState(moodMapCache || {});
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/moods").then((r) => {
      const m = {};
      (r.data || []).forEach((x) => { m[x.name] = x.lights || null; });
      moodMapCache = m;
      if (alive) setMap(m);
    }).catch(() => {});
    load();
    window.addEventListener("dtm-moods-changed", load);
    return () => { alive = false; window.removeEventListener("dtm-moods-changed", load); };
  }, [enabled]);
  return map;
}

const NO_PREFS = { mode: "song", custom: null, saved: [], songs: {} };
// a video from the member's own device (My Videos) has an id that starts with "local-": the site knows nothing about it
const isOwnFile = (id) => String(id).startsWith("local-");
// The lights a member gets for one song: what they chose for that song, else their usual setting.
export function lightsFor(prefs, songId) {
  const own = songId && prefs.songs ? prefs.songs[songId] : null;
  if (own) return { mode: own.mode, custom: own.custom || prefs.custom || null, own: true };
  return { mode: prefs.mode, custom: prefs.custom || null, own: false };
}
// A logged-in member's own choices (free or Premium): the song's lights / own colors / off, plus saved schemes.
// What they pick while a song plays is remembered for that song (and comes back when it plays again).
function useMyLights(enabled) {
  const [prefs, setPrefs] = useState(NO_PREFS);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const timer = useRef(null);
  const songTimers = useRef({});
  useEffect(() => {
    if (!enabled) { setPrefs(NO_PREFS); return undefined; }
    let alive = true;
    api.get("/me/lights").then((r) => {
      if (alive) setPrefs({ mode: r.data.mode || "song", custom: r.data.custom || null, saved: r.data.saved || [], songs: r.data.songs || {} });
    }).catch(() => {});
    return () => { alive = false; };
  }, [enabled]);
  // change the usual mode and/or own colors now; the server hears about it half a second after the last change
  const update = useCallback((patch) => {
    const next = { ...prefsRef.current, ...patch };
    prefsRef.current = next;
    setPrefs(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { api.put("/me/lights", { mode: next.mode, custom: next.custom }).catch(() => {}); }, 500);
  }, []);
  // change the lights for one song (mode and/or own colors) and remember them for it
  const updateSong = useCallback((songId, patch) => {
    if (!songId) return;
    const cur = lightsFor(prefsRef.current, songId);
    const entry = { mode: patch.mode !== undefined ? patch.mode : cur.mode, custom: patch.custom !== undefined ? patch.custom : cur.custom };
    const next = { ...prefsRef.current, songs: { ...prefsRef.current.songs, [songId]: entry } };
    prefsRef.current = next;
    setPrefs(next);
    clearTimeout(songTimers.current[songId]);
    if (isOwnFile(songId)) return;
    songTimers.current[songId] = setTimeout(() => { api.put(`/me/lights/songs/${songId}`, { mode: entry.mode, custom: entry.custom }).catch(() => {}); }, 500);
  }, []);
  // forget what was chosen for one song: it goes back to the member's usual lights
  const forgetSong = useCallback((songId) => {
    if (!songId) return;
    clearTimeout(songTimers.current[songId]);
    const songs = { ...prefsRef.current.songs };
    delete songs[songId];
    const next = { ...prefsRef.current, songs };
    prefsRef.current = next;
    setPrefs(next);
    if (isOwnFile(songId)) return;
    api.delete(`/me/lights/songs/${songId}`).catch(() => {});
  }, []);
  // make this song's lights the usual lights for every song that has none of its own
  const applyToAll = useCallback((songId) => {
    const cur = lightsFor(prefsRef.current, songId);
    update({ mode: cur.mode, custom: cur.custom });
    forgetSong(songId);
  }, [update, forgetSong]);
  const saveScheme = useCallback(async (name, scheme) => {
    const { data } = await api.post("/me/lights/schemes", { name, scheme });
    setPrefs((p) => ({ ...p, saved: data.saved || [] }));
  }, []);
  const deleteScheme = useCallback(async (id) => {
    const { data } = await api.delete(`/me/lights/schemes/${id}`);
    setPrefs((p) => ({ ...p, saved: data.saved || [] }));
  }, []);
  return { prefs, update, updateSong, forgetSong, applyToAll, saveScheme, deleteScheme };
}

// The color box under a wheel (for every logged-in member): it always shows the code of the color that is picked, and takes a typed
// color: a # code like #FFD700 (gold) or three numbers like 255 215 0 (red, green, blue). It changes the lights as
// soon as a whole code is typed; Enter or leaving the box takes whatever was typed, or goes back if it is not a color.
function ColorCodeBox({ rgb, onPick, label, testId }) {
  const shown = toHex(rgb).toUpperCase();
  const [text, setText] = useState(shown);
  const [focus, setFocus] = useState(false);
  const [bad, setBad] = useState(false);
  useEffect(() => { if (!focus) setText(shown); }, [shown, focus]);
  const whole = (t) => /^#?[0-9a-fA-F]{6}$/.test(t.trim()) || /^(rgb\s*\()?\s*\d{1,3}\s*[,; ]\s*\d{1,3}\s*[,; ]\s*\d{1,3}\s*\)?$/i.test(t.trim());
  const take = (t) => {
    const hex = parseColorCode(t);
    if (hex) { setBad(false); if (hex !== shown.toLowerCase()) onPick(hex); return true; }
    setBad(t.trim() !== "");
    return false;
  };
  return (
    <div className="flex w-full flex-col items-center gap-0.5">
      <input
        type="text" value={text} maxLength={24} spellCheck={false} autoComplete="off" autoCapitalize="characters" placeholder="#FFD700"
        aria-label={`${label}: color code`} aria-invalid={bad ? "true" : undefined} data-testid={testId}
        onFocus={(e) => { setFocus(true); setBad(false); e.target.select(); }}
        onChange={(e) => { setText(e.target.value); setBad(false); if (whole(e.target.value)) take(e.target.value); }}
        onBlur={() => { setFocus(false); take(text); }}
        onKeyDown={(e) => {
          if (e.key === "Escape") return;                       // Esc still closes the panel, as everywhere else
          e.stopPropagation();
          if (e.key === "Enter") { e.preventDefault(); take(text); e.currentTarget.blur(); }
        }}
        className={`w-[92px] rounded-md border bg-black/40 px-1 py-1.5 text-center font-mono text-[13px] text-white outline-none focus:border-cyan-300 ${bad ? "border-amber-400" : "border-white/25"}`}
      />
      <span className="text-[10px] text-zinc-500" data-testid={`${testId}-rgb`}>{rgb.join(", ")}</span>
      {bad && <span className="text-center text-[11px] leading-tight text-amber-300" role="alert" data-testid={`${testId}-bad`}>Not a color code. Try #FFD700 or 255 215 0.</span>}
    </div>
  );
}

// A color wheel: drag around the ring (or use the arrow keys) to pick a color. Under it, three
// round buttons give White, Grey and Black, which are not on the ring (red, white and blue,
// the German flag...). Touching the ring again goes back to a color from the ring.
// With `codes` (every logged-in member) there is also a box to type an exact color (see ColorCodeBox).
function HueWheel({ hue, onChange, label, testId, codes = false }) {
  const ref = useRef(null);
  const down = useRef(false);
  const neutral = neutralOf(hue);
  const rgb = hueColor(hue);
  const color = rgbCss(rgb);
  const angle = neutral ? null : isCode(hue) ? wheelAngle(rgb) : hue;     // where the knob sits (a typed color that is nearly grey has no place on the ring)
  const from = (e) => {
    const r = ref.current.getBoundingClientRect();
    const a = (Math.atan2(e.clientX - (r.left + r.width / 2), -(e.clientY - (r.top + r.height / 2))) * 180) / Math.PI;
    onChange(Math.round(a < 0 ? a + 360 : a) % 360);
  };
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        ref={ref} className="dtm-wheel" tabIndex={0} role="slider" aria-label={label}
        aria-valuemin={0} aria-valuemax={359} aria-valuenow={angle === null ? undefined : angle} aria-valuetext={neutral ? neutral.label : isCode(hue) ? hue.toUpperCase() : undefined}
        data-testid={testId} style={{ "--col": color }}
        onPointerDown={(e) => { down.current = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ } from(e); e.preventDefault(); }}
        onPointerMove={(e) => { if (down.current) from(e); }}
        onPointerUp={() => { down.current = false; }}
        onPointerCancel={() => { down.current = false; }}
        onKeyDown={(e) => {
          const d = e.key === "ArrowRight" || e.key === "ArrowUp" ? 5 : e.key === "ArrowLeft" || e.key === "ArrowDown" ? -5 : 0;
          if (d) { onChange(angle === null ? 0 : (angle + d + 360) % 360); e.preventDefault(); e.stopPropagation(); }
        }}
      >
        <i className="dtm-wheel-knob" style={{ transform: `rotate(${angle === null ? 0 : angle}deg) translateY(-33px)`, opacity: angle === null ? 0 : 1 }} />
        <b className="dtm-wheel-core" />
      </div>
      <div className="flex gap-1.5" data-testid={`${testId}-neutrals`}>
        {NEUTRALS.map((n) => (
          <button
            type="button" key={n.id} data-testid={`${testId}-${n.id}`} aria-label={`${label}: ${n.label}`} title={n.label} aria-pressed={hue === n.value}
            onClick={() => onChange(n.value)}
            className={`w-7 h-7 rounded-full p-0 border shrink-0 ${hue === n.value ? "border-cyan-300 ring-2 ring-cyan-300 ring-offset-1 ring-offset-zinc-900" : "border-white/40 hover:border-white"}`}
            style={{ background: rgbCss(n.rgb) }}
          />
        ))}
      </div>
      {codes && <ColorCodeBox rgb={rgb} label={label} testId={`${testId}-code`} onPick={onChange} />}
      <span className="text-[11px] text-zinc-400" data-testid={`${testId}-label`}>{neutral ? `${label}: ${neutral.label}` : label}</span>
    </div>
  );
}

// the color the fourth (between the waves) wheel starts on: opposite the first color on the ring
function blastStart(h0) {
  const rgb = hueColor(h0);
  const a = isCode(h0) ? wheelAngle(rgb) : neutralOf(h0) ? null : h0;
  return a === null ? 200 : (a + 180) % 360;
}

const segCls = (on) => `px-2.5 py-2 min-h-[40px] rounded-lg text-xs font-semibold border transition-colors text-left ${on ? "bg-cyan-400 text-black border-cyan-300" : "bg-white/5 border-white/10 text-zinc-300 hover:border-white/30"}`;

// What the Beat note under the speed bar says about the music (info comes from Backlight's onInfo).
function beatStatusLine(info) {
  if (!info) return null;
  if (info.status === "music") return { tone: "ok", text: "Hearing this song. The glow is following its beat." };
  if (info.status === "waiting") return { tone: "wait", text: "Listening to the song…" };
  if (info.status === "blocked") return { tone: "bad", text: "Beat can not hear this video's sound, so in the player the lights pulse at a steady tempo. The sample above shows what Beat does." };
  if (info.status === "unsupported") return { tone: "bad", text: "This browser does not let the page listen to the music, so in the player the lights pulse at a steady tempo. The sample above shows what Beat does." };
  return { tone: "wait", text: "The sample above uses a made-up beat. In the player the lights follow the real music." };
}

// The Beat strength meter: a wedge that grows thicker to the right, with a small knob on it. Slide the knob
// to the right and the blast goes out further.
function WedgeMeter({ value, onChange }) {
  const pct = ((value - 1) / 9) * 100;
  return (
    <span className="relative block h-8 w-[46%] min-w-[120px] rounded has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-cyan-300" data-testid="lights-reach-wedge">
      <span aria-hidden="true" className="absolute inset-x-0 bottom-1.5 block h-5"
        style={{ clipPath: "polygon(0 100%, 100% 100%, 100% 0)", background: `linear-gradient(90deg, #22d3ee ${pct}%, rgba(255,255,255,0.16) ${pct}%)` }} />
      <span aria-hidden="true" data-testid="lights-reach-knob" className="pointer-events-none absolute h-4 w-4 rounded-full border-2 border-cyan-400 bg-white shadow"
        style={{ left: `calc(${pct}% - 8px)`, bottom: `${(-2 + pct * 0.1).toFixed(1)}px` }} />
      <input type="range" min="1" max="10" step="1" value={value} aria-label="Beat strength" aria-valuetext={`${value} of 10`} data-testid="lights-reach"
        onChange={(e) => onChange(Number(e.target.value))} className="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0" />
    </span>
  );
}

// Pick a style, how many colors, the colors, the speed and the glow, and watch it on a small
// sample screen. Used by Admin (one per mood, allowOff) and by the member's lights panel.
// With the Beat speed a wedge under "Beat mode" sets how far the blast of light travels. In the member's panel
// the sample listens to the song that is playing (pass its `mediaRef`); otherwise it shows a made-up beat.
export function LightsEditor({ value, onChange, allowOff = false, testId = "lights-editor", mediaRef = null, paused = false, colorCodes }) {
  const sch = normScheme(value);
  const { user } = useAuth() || {};
  const codes = colorCodes === undefined ? !!user : !!colorCodes;     // the color code boxes are for every member who is logged in
  const [beatInfo, setBeatInfo] = useState(null);
  const set = (patch) => onChange(normScheme({ ...sch, ...patch }));
  const setHue = (i, h) => { const hues = sch.hues.slice(); hues[i] = h; set({ hues }); };
  // Steady has no Beat, so choosing it drops the speed to Max. Remember that the lights were on Beat, so choosing a moving
  // style again puts Beat back (otherwise a member who tries Steady and goes back finds the boom gone).
  const wasBeat = useRef(false);
  const pickStyle = (id) => {
    const still = id === "steady" || id === "off";
    if (still && sch.speed === BEAT) wasBeat.current = true;
    const patch = { style: id };
    if (!still && wasBeat.current) { patch.speed = BEAT; wasBeat.current = false; }
    set(patch);
  };
  // Picking an effect also sets matching colors (so the glow fits it); None only takes the effect away.
  const pickFx = (id) => {
    const f = FX_LIST.find((x) => x.id === id);
    if (f) set({ ...f.preset, fx: f.id }); else set({ fx: null });
  };
  const off = sch.style === "off";
  const styles = allowOff ? [{ id: "off", label: "No lights (standard look)" }, ...LIGHT_STYLES] : LIGHT_STYLES;
  const beatOn = sch.speed === BEAT && sch.style !== "steady" && !off;
  const status = beatOn ? beatStatusLine(beatInfo) : null;
  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="relative mx-auto my-3 h-[220px] w-full max-w-sm overflow-hidden rounded-xl bg-black/40" data-testid="lights-preview-stage">
        <div className="absolute left-1/2 top-1/2 -ml-[92px] -mt-[52px] h-[104px] w-[184px]" data-testid="lights-preview">
          {!off && <Backlight scheme={sch} demo mediaRef={mediaRef} paused={paused} room={{ x: 90, y: 50 }} onInfo={setBeatInfo} />}
          <div className="absolute inset-0 rounded-md bg-gradient-to-b from-indigo-900 via-fuchsia-900 to-orange-700 ring-1 ring-black shadow-[0_0_0_3px_#04070a,0_0_0_6px_#6f7882]" />
        </div>
      </div>
      <div>
        <div className="text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500 mb-1.5">Style</div>
        <div className="grid grid-cols-2 gap-1.5">
          {styles.map((s) => (
            <button type="button" key={s.id} data-testid={`lights-style-${s.id}`} aria-pressed={sch.style === s.id} onClick={() => pickStyle(s.id)} className={segCls(sch.style === s.id)}>{s.label}</button>
          ))}
        </div>
      </div>
      {!off && (
        <>
          <div>
            <label htmlFor={`${testId}-fx`} className="block text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500 mb-1.5">Effect</label>
            <select id={`${testId}-fx`} value={sch.fx || ""} onChange={(e) => pickFx(e.target.value)} data-testid="lights-fx"
              className="w-full rounded-lg border border-white/15 bg-zinc-900 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-300">
              <option value="">None</option>
              {fxSections(sch.fx).map((g) => (
                <optgroup key={g.id} label={g.label}>
                  {g.items.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </optgroup>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-zinc-400" data-testid="lights-fx-help">
              {sch.fx ? "Sparks, flames, snow, bats or ghosts come out from behind the screen. Picking one also sets matching colors; you can still change them below. The Glow slider makes it stronger or softer." : "Add frost, flames, bats or ghosts that come out from behind the screen."}
            </p>
          </div>
          <div>
            <div className="text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500 mb-1.5">Colors</div>
            <div className="grid grid-cols-3 gap-1.5 mb-3">
              {[["One color", 1], ["Two colors", 2], ["Many colors", 3]].map(([label, n]) => (
                <button type="button" key={n} data-testid={`lights-colors-${n}`} aria-pressed={sch.colors === n} onClick={() => set({ colors: n })} className={`${segCls(sch.colors === n)} text-center`}>{label}</button>
              ))}
            </div>
            <div className="flex justify-around gap-2">
              {Array.from({ length: sch.colors }, (_, i) => (
                <HueWheel key={i} hue={sch.hues[i]} label={`Color ${i + 1}`} testId={`lights-wheel-${i + 1}`} codes={codes} onChange={(h) => setHue(i, h)} />
              ))}
            </div>
            {codes && (
              <p className="mt-2 text-xs text-zinc-400" data-testid="lights-code-help">
                To get an exact color, type it in the box under a wheel: a # code like <b className="text-zinc-200">#FFD700</b> (gold), or red, green and blue numbers like <b className="text-zinc-200">255 215 0</b>. Then press Enter.
              </p>
            )}
          </div>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="flex justify-between font-semibold"><span>Speed</span><span className="text-zinc-400 font-normal" data-testid="lights-speed-out">{sch.style === "steady" ? "Steady" : SPEED_NAMES[sch.speed - 1]}</span></span>
              <input type="range" min="1" max={maxSpeed(sch.style)} step="1" value={sch.speed} disabled={sch.style === "steady"} data-testid="lights-speed"
                onChange={(e) => set({ speed: Number(e.target.value) })} className="w-full accent-cyan-400" />
              {sch.style !== "steady" && (
                <span className="relative block h-4 mt-0.5 text-[11px] text-zinc-500" aria-hidden="true" data-testid="lights-speed-marks">
                  <span className="absolute left-0" data-testid="lights-mark-slow">Slow</span>
                  <span className="absolute pr-1" style={{ right: "20%" }} data-testid="lights-mark-max">Max</span>
                  <span className={`absolute right-0 ${sch.speed === BEAT ? "text-cyan-300 font-semibold" : ""}`} data-testid="lights-mark-beat">Beat mode</span>
                </span>
              )}
            </label>
            {beatOn && (
              <div data-testid="lights-beat-box">
                <div className="flex items-end justify-end gap-3">
                  <span className="whitespace-nowrap pb-0.5 text-[11px] text-zinc-400">Strength <b className="text-zinc-200" data-testid="lights-reach-out">{sch.reach}</b></span>
                  <WedgeMeter value={sch.reach} onChange={(n) => set({ reach: n })} />
                </div>
                <span className="block text-xs text-zinc-400 mt-1" data-testid="lights-beat-note">
                  {sch.style.startsWith("maypole") ? "The lights keep turning and a blast of light goes out with each beat of the music, farther when the bass hits harder." : "A blast of light goes out with each beat of the music, farther when the bass hits harder."} Slide the knob to the right to make the blasts go farther. The lights follow the song's tempo when it changes. If the music can not be read, they pulse at a steady tempo instead.
                </span>
                <div className="mt-3" data-testid="lights-blast-box">
                  <div className="text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500 mb-1.5">Color between the waves</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button type="button" data-testid="lights-blast-none" aria-pressed={sch.blast === null} onClick={() => set({ blast: null })} className={`${segCls(sch.blast === null)} text-center`}>None</button>
                    <button type="button" data-testid="lights-blast-pick" aria-pressed={sch.blast !== null} onClick={() => { if (sch.blast === null) set({ blast: blastStart(sch.hues[0]) }); }} className={`${segCls(sch.blast !== null)} text-center`}>Pick a color</button>
                  </div>
                  {sch.blast !== null && (
                    <div className="mt-3 flex justify-center">
                      <HueWheel hue={sch.blast} label="Between the waves" testId="lights-wheel-blast" codes={codes} onChange={(h) => set({ blast: h })} />
                    </div>
                  )}
                  {sch.blast !== null && (
                    <label className="mt-3 block text-sm">
                      <span className="flex justify-between font-semibold"><span>Opacity</span><span className="text-zinc-400 font-normal" data-testid="lights-blast-op-out">{sch.blastOp}%</span></span>
                      <input type="range" min="10" max="100" step="5" value={sch.blastOp} data-testid="lights-blast-op" aria-label="Opacity of the color between the waves"
                        onChange={(e) => set({ blastOp: Number(e.target.value) })} className="w-full accent-cyan-400" />
                    </label>
                  )}
                  <span className="block text-xs text-zinc-400 mt-1" data-testid="lights-blast-note">
                    {sch.blast === null ? "The space between the waves stays dark." : "This color fills the glow around each blast and the thin gap between its stripes. Slide Opacity to make it stronger or softer."}
                  </span>
                </div>
                {status && (
                  <span className={`block text-xs mt-1.5 font-semibold ${status.tone === "ok" ? "text-emerald-300" : status.tone === "bad" ? "text-amber-300" : "text-zinc-400"}`} data-testid="lights-beat-status" data-tone={status.tone}>
                    {status.text}
                  </span>
                )}
              </div>
            )}
            <label className="block text-sm">
              <span className="flex justify-between font-semibold"><span>Glow</span><span className="text-zinc-400 font-normal" data-testid="lights-glow-out">{sch.glow}%</span></span>
              <input type="range" min="30" max="100" step="5" value={sch.glow} data-testid="lights-glow"
                onChange={(e) => set({ glow: Number(e.target.value) })} className="w-full accent-cyan-400" />
            </label>
          </div>
        </>
      )}
    </div>
  );
}

// "Add to playlist": a small list of the member's playlists over a song (the song keeps
// playing). It opens from the + on the big player, the little bar at the bottom, TV mode, the song cards and the song's own
// page. Inside the big player it sits over the picture; from anywhere else (`fixed`) it floats over the whole page. Tap a playlist to add the song. A free member's playlist that already has the song says so (no repeats for free);
// a Premium member's playlist can hold the same song more than once, so it stays pressable and adds another copy. A box at the
// bottom makes a new playlist and adds the song to it. The free-member rules are the same as on the song page: one playlist,
// 10 songs in it.
export function AddToPlaylistPanel({ song, many, onClose, fixed = false }) {
  const [lists, setLists] = useState(null);
  const [busy, setBusy] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState(null);
  const [added, setAdded] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/playlists").then((r) => { if (alive) setLists(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) { setLists([]); setMsg({ ok: false, text: "Could not load your playlists. Close this and try again." }); } });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const copies = (pl) => (pl.video_ids || []).filter((x) => x === song.id).length;       // how many times the song is in it
  const has = (pl) => copies(pl) > 0 || added.includes(pl.id);
  const mark = (pl) => {
    setAdded((a) => [...a, pl.id]);
    setLists((ls) => (ls || []).map((x) => (x.id === pl.id ? { ...x, video_ids: [...(x.video_ids || []), song.id], item_count: (x.item_count || 0) + 1 } : x)));
  };
  const failed = (err) => {
    const d = err && err.response && err.response.data && err.response.data.detail;
    if (!["premium_required", "email_not_verified"].includes(d)) setMsg({ ok: false, text: "Could not do that. Try again." });
  };
  const add = async (pl) => {
    setMsg(null);
    if (!many && (pl.item_count || 0) >= 10) { showUpgradeExternally("Free playlists are limited to 10 songs"); return; }
    setBusy(pl.id);
    const another = copies(pl) > 0;
    try {
      await api.post(`/playlists/${pl.id}/items`, { video_id: song.id });
      mark(pl);
      toast.success(another ? `Added another copy to "${pl.name}"` : `Added to "${pl.name}"`);
    } catch (err) { failed(err); }
    finally { setBusy(""); }
  };
  const create = async () => {
    const n = name.trim();
    setMsg(null);
    if (!n) { setMsg({ ok: false, text: "Type a name for the new playlist first." }); return; }
    if (!many && (lists || []).length >= 1) { askForPremium("Free accounts can have one playlist"); return; }
    setBusy("new");
    let made = null;
    try {
      const r = await api.post("/playlists", { name: n });
      made = { ...r.data, video_ids: [], item_count: 0 };
      setLists((ls) => [made, ...(ls || [])]);
      await api.post(`/playlists/${made.id}/items`, { video_id: song.id });
      mark(made);
      setName("");
      toast.success(`Made "${n}" and added the song`);
    } catch (err) { failed(err); }
    finally { setBusy(""); }
  };
  const box = (
    <div className={`${fixed ? "fixed inset-0 z-[60]" : "absolute inset-0 z-30"} flex items-end sm:items-center justify-center bg-black/60 p-3`} onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="addpl-backdrop">
      <div role="dialog" aria-modal="true" aria-label="Add to playlist" data-testid="addpl-panel"
        className="w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border border-white/15 bg-zinc-900 p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-xl font-bold">Add to playlist</h2>
          <button type="button" data-testid="addpl-close" onClick={onClose} className="px-3 py-1.5 rounded-full border border-white/15 text-sm hover:bg-white/5">Done</button>
        </div>
        <p className="text-sm text-zinc-400 mb-3 truncate" data-testid="addpl-song"><b className="text-zinc-200">{song.title}</b>{song.artist ? ` • ${song.artist}` : ""}</p>
        {lists === null && <div className="py-4 text-sm text-zinc-500" data-testid="addpl-loading">Loading your playlists…</div>}
        {lists !== null && lists.length === 0 && <div className="py-3 text-sm text-zinc-400" data-testid="addpl-empty">You have no playlists yet. Name one below and the song goes in it.</div>}
        {lists !== null && lists.length > 0 && (
          <ul className="space-y-1.5 mb-3" data-testid="addpl-list">
            {lists.map((pl) => {
              const inIt = has(pl);
              const n = copies(pl);
              const locked = inIt && !many;                       // a free member's playlist takes the song once
              return (
                <li key={pl.id}>
                  <button type="button" data-testid={`addpl-${pl.id}`} disabled={locked || busy === pl.id} onClick={() => add(pl)} data-in={inIt ? "yes" : "no"}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 min-h-[44px] rounded-lg border text-left text-sm ${locked ? "border-emerald-400/30 bg-emerald-400/5 text-zinc-400 cursor-default" : inIt ? "border-emerald-400/30 bg-emerald-400/5 hover:border-cyan-300/50 hover:bg-white/10" : "border-white/10 bg-white/5 hover:border-cyan-300/50 hover:bg-white/10"}`}>
                    <span className="min-w-0"><span className="block truncate font-semibold text-zinc-100">{pl.name}</span><span className="block text-xs text-zinc-500">{pl.item_count || 0} {(pl.item_count || 0) === 1 ? "song" : "songs"}{many && n > 0 ? ` · this song is in it${n > 1 ? ` ×${n}` : ""}` : ""}</span></span>
                    <span className={`shrink-0 text-xs font-semibold ${inIt ? "text-emerald-300" : "text-cyan-300"}`}>{busy === pl.id ? "Adding…" : many ? (n > 0 ? (added.includes(pl.id) ? "Added ✓ · add another" : "Add another") : "Add") : added.includes(pl.id) ? "Added ✓" : inIt ? "Already in it" : "Add"}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); create(); }}>
          <input type="text" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Name for a new playlist" aria-label="Name for a new playlist" data-testid="addpl-new-name"
            onKeyDown={(e) => { if (e.key !== "Escape") e.stopPropagation(); }}
            className="min-w-0 flex-1 rounded-lg border border-white/20 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-300" />
          <button type="submit" data-testid="addpl-create" disabled={busy === "new" || lists === null} className="shrink-0 px-3 py-2 min-h-[40px] rounded-lg bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-50">Create and add</button>
        </form>
        {msg && <div className={`mt-2 text-sm ${msg.ok ? "text-emerald-300" : "text-amber-300"}`} role="alert" data-testid="addpl-msg">{msg.text}</div>}
      </div>
    </div>
  );
  return fixed && typeof document !== "undefined" ? createPortal(box, document.body) : box;
}

// The top strip of a settings panel: the title, a close X (a computer click), and on a touch screen a small grab bar.
// Swiping the strip away closes the panel: down on a phone or tablet, to the right when the panel sits at the right edge
// of a sideways phone. A TV's Back button closes it too (see isBackKey, used by the panels' key handlers).
function PanelHead({ title, onClose, closeId, swipe = "down", plate = null }) {
  const start = useRef(null);
  const onDown = (e) => { start.current = e.pointerType === "touch" ? { x: e.clientX, y: e.clientY } : null; };
  const onUp = (e) => {
    const s = start.current; start.current = null;
    if (!s) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (swipe === "right" ? (dx > 60 && Math.abs(dx) > Math.abs(dy)) : (dy > 50 && Math.abs(dy) > Math.abs(dx))) onClose();
  };
  return (
    <div data-testid="panel-head" onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => { start.current = null; }} style={{ touchAction: "none" }}>
      <div className="hidden [@media(pointer:coarse)]:flex justify-center pb-2" aria-hidden="true"><span className="h-1 w-10 rounded-full bg-white/25" /></div>
      <div className={`flex items-center ${plate ? "gap-2.5 mb-3.5" : "justify-between mb-2"}`}>
        {plate || <h2 className="font-display text-xl font-bold">{title}</h2>}
        <button type="button" data-testid={closeId} aria-label="Close" onClick={onClose} className="w-9 h-9 shrink-0 grid place-items-center rounded-full border border-white/15 text-zinc-200 hover:bg-white/10">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" /></svg>
        </button>
      </div>
    </div>
  );
}

// Where a box sits inside its area, so the lights can be drawn around it out to the edges of the screen:
// {l, r, t, b} = the free room on each side (px). null while the lights are off.
function useGlowRoom(active, boxRef, areaRef) {
  const [room, setRoom] = useState(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const area = areaRef.current;
    if (!active || !box || !area) { setRoom(null); return undefined; }
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const b = box.getBoundingClientRect();
        const a = area.getBoundingClientRect();
        if (!b.width || !b.height) { setRoom(null); return; }
        const next = { l: Math.round(b.left - a.left), r: Math.round(a.right - b.right), t: Math.round(b.top - a.top), b: Math.round(a.bottom - b.bottom) };
        setRoom((old) => (old && old.l === next.l && old.r === next.r && old.t === next.t && old.b === next.b ? old : next));
      });
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) { ro.observe(box); ro.observe(area); }
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [active, boxRef, areaRef]);
  return room;
}

// The look of the song box: a thin brushed-steel bezel around a dark box, and the two steel plates with cut-out words
// (the song and artist at the top, "Lyrics" above the words). The colors are the page's light colors (--lt-...), which
// follow the song's lights or the member's own colors, so the bezel ring and the plates glow in the same color.
const SONG_INFO_CSS = `
.dtm-si-bezel { position: relative; width: 100%; display: flex; flex-direction: column; min-height: 0; padding: 5px; border-radius: 21px;
  background: repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, rgba(0,0,0,.06) 1px 2px), linear-gradient(135deg, #f4f7fa 0%, #b3bcc5 20%, #737d88 46%, #cdd4db 68%, #5b646e 100%);
  box-shadow: inset 0 1px 1px rgba(255,255,255,.85), inset 0 -2px 3px rgba(0,0,0,.5), 0 0 0 1px #04070a, 0 0 0 2px rgba(var(--lt-rgb, 0, 240, 255), .8), 0 0 20px rgba(var(--lt-rgb, 0, 240, 255), .32); }
.dtm-si-box { position: relative; flex: 0 1 auto; min-width: 0; min-height: 0; display: flex; flex-direction: column; border-radius: 16px; background: #18181b;
  box-shadow: 0 0 0 1px #04070a, inset 0 0 16px rgba(0,0,0,.65); padding: 16px; }
.dtm-si-box > div:first-child { flex: none; }
.dtm-si-scroll { min-height: 0; overflow-y: auto; padding-right: 4px; font-size: 14px; line-height: 1.65; color: #e4e4e7; overscroll-behavior: contain; }
.dtm-si-scroll::-webkit-scrollbar { width: 6px; }
.dtm-si-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,.22); border-radius: 3px; }
.dtm-si-title { flex: 1 1 auto; min-width: 0; justify-content: center; padding: 10px 28px; min-height: 44px; }
.dtm-si-title .cn { font-size: 15px; letter-spacing: .08em; }
.dtm-si-title .cn-glow, .dtm-si-pill .cn-glow { filter: blur(3px); }
.dtm-si-pill { padding: 0 20px; height: 30px; border-radius: 9999px; }
.dtm-si-pill::after { display: none; }
.dtm-si-pill .cn { font-size: 11px; letter-spacing: .14em; }
@media (max-width: 639px) {
  .dtm-si-title { padding: 9px 24px; }
  .dtm-si-title .cn { font-size: 13px; letter-spacing: .05em; }
}
`;

// The song's description and lyrics, read from the big player or from the little bar (the square "i" button beside Share, or
// a press on the song card in the big player). One scrolling box: the description first, then a "Lyrics" plate and the words.
// While the song plays, its lights (the glow and the effects) come off the bezel the same way they come off the picture.
//   scheme - the lights to draw around the box (null = none: lights off, or a full screen)
function SongInfoPanel({ song, fixed = false, onClose, scheme = null, playing = false, mediaRef = null }) {
  useEffect(() => {
    const onKey = (e) => { if (isBackKey(e)) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const id = song.id;
  const [lyr, setLyr] = useState({ id: null, text: "" });   // the words are asked for on their own: the server leaves them out of an explicit song until the 18+ check is passed
  useEffect(() => {
    let dead = false;
    api.get(`/videos/${id}/lyrics`)
      .then((r) => { if (!dead) setLyr({ id, text: String((r.data && r.data.lyrics) || "").trim() }); })
      .catch(() => { if (!dead) setLyr({ id, text: "" }); });
    return () => { dead = true; };
  }, [id]);
  const ready = lyr.id === id;
  const words = ready ? lyr.text : "";
  const about = typeof song.description === "string" ? song.description.trim() : "";
  const headline = song.artist ? `${song.title} | ${song.artist}` : String(song.title || "");
  const holderRef = useRef(null);
  const areaRef = useRef(null);
  const room = useGlowRoom(!!scheme, holderRef, areaRef);
  const bounds = useMemo(() => (room ? { l: Math.max(0, room.l), r: Math.max(0, room.r), t: Math.max(0, room.t), b: Math.max(0, room.b) } : null), [room]);
  const blast = useMemo(() => (room ? { x: Math.max(24, Math.min(room.l, room.r)), y: Math.max(16, Math.min(room.t, room.b)) } : null), [room]);
  const box = (
    <div ref={areaRef} className={`${fixed ? "fixed inset-0 z-[60]" : "absolute inset-0 z-30"} flex items-end sm:items-center justify-center overflow-hidden bg-black/60 p-3`} onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="song-info-backdrop">
      <style>{SONG_INFO_CSS}</style>
      <div ref={holderRef} className="relative w-full max-w-md min-h-0 max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {scheme && (
          <div className="absolute inset-0 pointer-events-none" data-testid="song-info-lights">
            <Backlight scheme={scheme} paused={!playing} mediaRef={mediaRef} room={blast} bounds={bounds} />
          </div>
        )}
        <div role="dialog" aria-modal="true" aria-label="About this song" data-testid="song-info-panel" className="dtm-si-bezel">
          <div className="dtm-si-box">
            <PanelHead onClose={onClose} closeId="song-info-close"
              plate={<span className="steel-plate dtm-si-title relative inline-flex items-center" data-testid="song-info-song"><CutLetters text={headline} /></span>} />
            <div className="dtm-si-scroll" data-testid="song-info-scroll">
              {about && <div data-testid="song-info-text" className="whitespace-pre-wrap break-words">{about}</div>}
              {words && (
                <>
                  <h3 className={`flex justify-center ${about ? "mt-5" : "mt-1"} mb-3`} data-testid="song-info-lyrics-head">
                    <span className="steel-plate dtm-si-pill relative inline-flex items-center justify-center"><CutLetters text="Lyrics" /></span>
                  </h3>
                  <div data-testid="song-info-lyrics" className="whitespace-pre-line break-words text-zinc-300">{words}</div>
                </>
              )}
              {!about && !words && (
                <p className="text-zinc-400" data-testid="song-info-empty">{ready ? "There is no description or lyrics for this song yet." : "Loading…"}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return fixed && typeof document !== "undefined" ? createPortal(box, document.body) : box;
}

// The member's lights panel (opens from the light bulb button in the big player). Whatever is picked here is
// remembered for the song that is playing, and comes back the next time that song plays.
function LightsPanel({ onClose, prefs, songId, songTitle, updateSong, forgetSong, applyToAll, saveScheme, deleteScheme, songScheme, songMood, mediaRef, playing }) {
  const [name, setName] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (isBackKey(e)) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const mine = lightsFor(prefs, songId);
  const custom = normScheme(mine.custom || songScheme || STANDARD_SCHEME);
  const pick = (mode) => {
    if (mode === "custom") updateSong(songId, { mode, custom: mine.custom || songScheme || STANDARD_SCHEME });
    else updateSong(songId, { mode });
    setMsg(null);
  };
  const save = async () => {
    setBusy(true); setMsg(null);
    try { await saveScheme(name, custom); setMsg({ ok: true, text: `Saved "${name.trim()}".` }); setName(""); }
    catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Could not save that. Try again." }); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { try { await deleteScheme(id); } catch { setMsg({ ok: false, text: "Could not delete that. Try again." }); } };
  const modes = [["song", "The song's lights"], ["custom", "My colors"], ["off", "Lights off"]];
  const small = "px-3 py-2 rounded-lg border border-white/15 text-xs font-semibold hover:bg-white/10";
  return (
    <div className="absolute inset-0 z-30 flex items-end sm:items-center justify-center bg-black/60 p-3" onClick={onClose} data-testid="lights-panel-backdrop">
      <div role="dialog" aria-modal="true" aria-label="Lights" data-testid="lights-panel"
        className="w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border border-white/15 bg-zinc-900 p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <PanelHead title="Lights" onClose={onClose} closeId="lights-panel-close" />
        <p className="text-sm text-zinc-400 mb-3" data-testid="lights-song-line" data-remembered={mine.own ? "yes" : "no"}>
          {mine.own
            ? <><span className="text-emerald-300 font-semibold">Remembered for this song.</span> <b className="text-zinc-200">{songTitle}</b> gets these lights every time it plays.</>
            : <>Change anything here and it is remembered for <b className="text-zinc-200">{songTitle}</b>, so it comes back the next time this song plays.</>}
        </p>
        {mine.own && (
          <div className="flex flex-wrap gap-2 mb-3">
            <button type="button" data-testid="lights-use-all" className={small}
              onClick={() => { applyToAll(songId); setMsg({ ok: true, text: "Done. Every song you have not set yourself now uses these lights." }); }}>Use for all my songs</button>
            <button type="button" data-testid="lights-forget-song" className={small}
              onClick={() => { forgetSong(songId); setMsg({ ok: true, text: "Forgot this song's lights. It uses your usual lights again." }); }}>Forget this song's lights</button>
          </div>
        )}
        <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid="lights-modes">
          {modes.map(([id, label]) => (
            <button type="button" key={id} data-testid={`lights-mode-${id}`} aria-pressed={mine.mode === id} onClick={() => pick(id)} className={`${segCls(mine.mode === id)} text-center`}>{label}</button>
          ))}
        </div>
        {mine.mode === "song" && (
          <p className="text-sm text-zinc-400 mb-1" data-testid="lights-song-note">
            {songScheme && songScheme.style !== "off"
              ? <>Showing the lights for the mood <b className="text-zinc-200">{songMood}</b>. <SchemeDot scheme={songScheme} /></>
              : "This song has no mood lights, so you see the standard look."}
          </p>
        )}
        {mine.mode === "off" && <p className="text-sm text-zinc-400 mb-1">The lights are off. Everything stays the standard blue.</p>}
        {mine.mode === "custom" && (
          <>
            <LightsEditor value={custom} onChange={(v) => updateSong(songId, { custom: v })} testId="lights-own-editor" mediaRef={mediaRef} paused={!playing} />
            <div className="mt-4 flex gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} placeholder="Name these colors" data-testid="lights-save-name"
                className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" />
              <button type="button" disabled={busy || !name.trim()} data-testid="lights-save-btn" onClick={save}
                className="px-3 py-2 rounded-lg bg-cyan-400 text-black text-sm font-semibold disabled:opacity-40">Save these colors</button>
            </div>
          </>
        )}
        {msg && <p className={`mt-2 text-sm ${msg.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="lights-msg">{msg.text}</p>}
        <div className="mt-4">
          <div className="text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500 mb-1.5">My saved colors ({prefs.saved.length})</div>
          {prefs.saved.length === 0 && <p className="text-sm text-zinc-500" data-testid="lights-saved-empty">Nothing saved yet. Pick My colors, make something you like, name it, and save it.</p>}
          <ul className="space-y-1.5" data-testid="lights-saved-list">
            {prefs.saved.map((s) => (
              <li key={s.id} className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 pl-3 pr-1 py-1" data-testid={`lights-saved-${s.name}`}>
                <SchemeDot scheme={s.scheme} />
                <span className="flex-1 min-w-0 truncate text-sm">{s.name}</span>
                <button type="button" data-testid={`lights-use-${s.name}`} onClick={() => updateSong(songId, { mode: "custom", custom: normScheme(s.scheme) })} className="px-2.5 py-1.5 rounded-md text-xs font-semibold border border-white/15 hover:bg-white/10">Use</button>
                <button type="button" data-testid={`lights-delete-${s.name}`} aria-label={`Delete ${s.name}`} onClick={() => remove(s.id)} className="px-2 py-1.5 rounded-md text-xs text-zinc-400 hover:text-red-300 hover:bg-white/10">Delete</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- Caption bar ----
// A thin steel plate under (or above) the picture with the song's words cut out of it and lit from behind. It has its own
// room, so the words never sit on the picture. The little square button at its right end opens the settings (CaptionsPanel).
// `geo` places the plate; `h` is its thickness; `vars` are the --lt-* colors of the light; `ink` is a black light (black words).
function CaptionBar({ text, geo, h, vars, ink, words, glow, visible, onOpen, over = false, fx = null, paused = false }) {
  const barRef = useRef(null);
  const cnRef = useRef(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    const cn = cnRef.current;
    if (!bar || !cn) return;
    // the biggest size that fits: one line if it can, else two (smaller words), and a very thin bar gets small words
    let size = Math.min(Math.max(8, bar.clientHeight * 0.52), Math.max(14, bar.clientWidth / 30), 30);
    cn.style.setProperty("--cs", `${size}px`);
    const cs = window.getComputedStyle(bar);
    const room = bar.clientHeight - Math.min(8, bar.clientHeight * 0.15);
    const side = bar.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    let guard = 24;
    while (guard > 0 && size > 7 && (cn.scrollHeight > room || cn.scrollWidth > side + 1)) {
      guard -= 1; size -= 1;
      cn.style.setProperty("--cs", `${size}px`);
    }
  }, [text, h, geo.width, words]);
  return (
    <div ref={barRef} data-testid="caption-bar" data-words={words} data-ink={ink ? "1" : "0"} data-over={over ? "1" : "0"}
      className={`${over ? "over " : "steel-plate "}cap-bar${ink ? " ink" : ""}${words === "plain" ? " plain" : ""}`}
      style={{ ...geo, ...vars, height: h, "--barh": `${h}px`, "--cap-op": glow / 100 }}>
      {fx && !over && <BarFx fx={fx} paused={paused} />}
      <span ref={cnRef} className="cn" aria-hidden="true" data-testid="caption-text">
        <span className="cn-glow">{text}</span>
        <span className="cn-base">{text}</span>
        <span className="cn-lit">{text}</span>
        <span className="cn-lip">{text}</span>
      </span>
      {!text && <i className="cap-empty" aria-hidden="true" />}
      <span className={`cap-style-wrap transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <button type="button" data-testid="captions-style-btn" onClick={onOpen} aria-label="Caption settings" title="Caption settings" className="steel-btn cap-style-btn">
          <SteelFace glyph="sliders" />
        </button>
      </span>
    </div>
  );
}

// The caption settings. They are kept on this device (see lib/captions.js), so a phone and a computer can differ.
// It stays off to the side of the bar, so the bar is always in view while a setting is changed.
function CaptionsPanel({ style, setStyle, barH, hue, canMatch, codes, awayFromTop, sideways, strip, room, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (isBackKey(e)) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const modes = canMatch
    ? [["song", "The song's lights"], ["match", "Match my lights"], ["mine", "My own color"]]
    : [["song", "The song's lights"], ["mine", "My own color"]];
  const pickMode = (m) => setStyle(m === "mine" ? { color: "mine", hue } : { color: m });
  const label = "block text-sm font-semibold";
  return (
    <div data-testid="captions-panel-wrap"
      className={sideways ? "absolute right-2 z-30 pointer-events-none" : `absolute inset-x-0 z-30 flex justify-center px-3 pointer-events-none ${awayFromTop ? "items-start" : "items-end"}`}
      style={sideways ? (awayFromTop ? { top: strip + 8, bottom: 8 } : { top: 8, bottom: strip + 8 }) : room}>
      <div role="dialog" aria-label="Captions" data-testid="captions-panel" onClick={(e) => e.stopPropagation()}
        className={`pointer-events-auto overflow-y-auto rounded-2xl border border-white/15 bg-zinc-900 p-4 shadow-2xl ${sideways ? "h-full w-72 max-w-[calc(100vw-1rem)]" : "w-full max-w-sm max-h-full"}`}>
        <PanelHead title="Captions" onClose={onClose} closeId="captions-panel-close" swipe={sideways ? "right" : "down"} />
        <p className="text-xs text-zinc-400 mb-3">By default the light is the color set for the song. Match my lights copies whatever the Lights panel is showing. My own color sets the captions separately.</p>
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="captions-colors">
          {modes.map(([id, text]) => (
            <button type="button" key={id} data-testid={`captions-color-${id}`} aria-pressed={style.color === id} onClick={() => pickMode(id)} className={`${segCls(style.color === id)} text-center`}>{text}</button>
          ))}
        </div>
        <div className={`mb-3 ${style.color === "mine" ? "" : "opacity-50"}`}>
          <HueWheel hue={hue} onChange={(h) => setStyle({ color: "mine", hue: h })} label="Caption color" testId="captions-wheel" codes={codes} />
        </div>
        <label className="mb-3 block">
          <span className={`${label} flex justify-between`}>Glow<b className="font-normal text-zinc-400" data-testid="captions-glow-out">{style.glow}%</b></span>
          <input type="range" min="20" max="100" step="5" value={style.glow} data-testid="captions-glow" aria-label="Glow"
            onChange={(e) => setStyle({ glow: Number(e.target.value) })} className="w-full accent-cyan-400" />
        </label>
        <label className="mb-3 block">
          <span className={`${label} flex justify-between`}>Size<b className="font-normal text-zinc-400" data-testid="captions-size-out">{Math.round(((barH - BAR_MIN) / (BAR_MAX - BAR_MIN)) * 100)}%</b></span>
          <input type="range" min={BAR_MIN} max={BAR_MAX} step="2" value={barH} data-testid="captions-size" aria-label="Size"
            onChange={(e) => setStyle({ size: Number(e.target.value) })} className="w-full accent-cyan-400" />
        </label>
        <div className="mb-3">
          <b className={label}>Where the bar sits</b>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {[["bottom", "Below the picture"], ["top", "Above the picture"]].map(([id, text]) => (
              <button type="button" key={id} data-testid={`captions-pos-${id}`} aria-pressed={style.pos === id} onClick={() => setStyle({ pos: id })} className={`${segCls(style.pos === id)} text-center`}>{text}</button>
            ))}
          </div>
        </div>
        <div className="mb-3">
          <b className={label}>Bar effects</b>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {[[true, "On"], [false, "Off"]].map(([v, text]) => (
              <button type="button" key={text} data-testid={`captions-fx-${v ? "on" : "off"}`} aria-pressed={style.fx === v} onClick={() => setStyle({ fx: v })} className={`${segCls(style.fx === v)} text-center`}>{text}</button>
            ))}
          </div>
        </div>
        <div className="mb-3">
          <b className={label}>Words style</b>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {[["steel", "Steel cut-out"], ["plain", "Plain words"]].map(([id, text]) => (
              <button type="button" key={id} data-testid={`captions-words-${id}`} aria-pressed={style.words === id} onClick={() => setStyle({ words: id })} className={`${segCls(style.words === id)} text-center`}>{text}</button>
            ))}
          </div>
        </div>
        <button type="button" data-testid="captions-reset" onClick={() => setStyle({ ...DEFAULT_STYLE })}
          className="px-3 py-2 rounded-lg border border-white/15 text-xs font-semibold hover:bg-white/10">Back to the song's lights</button>
        <p className="mt-2 text-[11px] text-zinc-500">Kept on this device.</p>
      </div>
    </div>
  );
}

// What a guest or a free member sees when they tap the light bulb: a live sample of the lights (the Beat blast,
// a Maypole, a Heartbeat, one after the other) and the way to sign up.
const TEASER_LOOKS = [
  { label: "Beat: blasts of light go out on the bass", scheme: { style: "pulse", colors: 2, hues: [330, 28, 28], speed: 6, glow: 90, reach: 8 } },
  { label: "Maypole: the colors chase around the picture", scheme: { style: "maypole-cw", colors: 3, hues: [187, 300, 48], speed: 3, glow: 85, reach: 6 } },
  { label: "Heartbeat: slow and warm", scheme: { style: "heartbeat", colors: 1, hues: [350, 350, 350], speed: 3, glow: 85, reach: 6 } },
];
function LightsTeaser({ guest, onClose, onGo }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % TEASER_LOOKS.length), 5200);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { clearInterval(t); document.removeEventListener("keydown", onKey); };
  }, [onClose]);
  const look = TEASER_LOOKS[i];
  const title = guest ? "Create a free login" : "Lights are a Premium feature";
  return (
    <div className="absolute inset-0 z-30 flex items-end sm:items-center justify-center bg-black/60 p-3" onClick={onClose} data-testid="lights-teaser-backdrop">
      <div role="dialog" aria-modal="true" aria-label={title} data-testid="lights-teaser"
        className="w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border border-amber-400/40 bg-gradient-to-br from-zinc-900 via-zinc-900 to-amber-500/5 p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="font-mono-ui text-xs uppercase tracking-widest text-amber-400 mb-2">Premium only</div>
        <h2 className="font-display text-2xl font-extrabold mb-2" data-testid="lights-teaser-title">{title}</h2>
        <div className="relative mx-auto my-2 h-[230px] w-full overflow-hidden rounded-xl bg-black/50" data-testid="lights-teaser-stage">
          <div className="absolute left-1/2 top-1/2 -ml-[104px] -mt-[58px] h-[117px] w-[208px]">
            <Backlight key={i} scheme={look.scheme} demo room={{ x: 100, y: 55 }} />
            <div className="absolute inset-0 rounded-md bg-gradient-to-b from-indigo-900 via-fuchsia-900 to-orange-700 ring-1 ring-black shadow-[0_0_0_3px_#04070a,0_0_0_6px_#6f7882]" />
          </div>
        </div>
        <p className="text-center text-sm font-semibold text-cyan-300 mb-3" data-testid="lights-teaser-caption">{look.label}</p>
        <p className="text-sm text-zinc-400 mb-5" data-testid="lights-teaser-body">
          {guest ? "Sign up, and go Premium, to " : "Premium members "}light up the player in the colors of each song's mood, pick their own colors, and set a light show for each song that the player remembers the next time it plays.
        </p>
        <div className="flex gap-2 justify-end">
          <button type="button" data-testid="lights-teaser-not-now" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-sm hover:bg-white/5">Not now</button>
          <Link to={guest ? "/register" : "/account?checkout=1"} data-testid="lights-teaser-cta" onClick={onGo}
            className="px-4 py-2 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300">{guest ? "Create a free login" : "Sign up for Premium"}</Link>
        </div>
      </div>
    </div>
  );
}

// Where the video box sits inside the big player, so the lights can be drawn right behind it.
function useVideoBox(active, videoRef, overlayRef, again) {
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    const video = videoRef.current;
    const overlay = overlayRef.current;
    if (!active || !video || !overlay) { setBox(null); return undefined; }
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const v = video.getBoundingClientRect();
        const o = overlay.getBoundingClientRect();
        if (!v.width || !v.height) { setBox(null); return; }
        const next = { l: Math.round(v.left - o.left), t: Math.round(v.top - o.top), w: Math.round(v.width), h: Math.round(v.height), ow: Math.round(o.width), oh: Math.round(o.height) };
        setBox((old) => (old && old.l === next.l && old.t === next.t && old.w === next.w && old.h === next.h && old.ow === next.ow && old.oh === next.oh ? old : next));
      });
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) { ro.observe(video); ro.observe(overlay); }
    window.addEventListener("resize", measure);
    video.addEventListener("loadedmetadata", measure);
    video.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
      video.removeEventListener("loadedmetadata", measure);
      video.removeEventListener("resize", measure);
    };
  }, [active, videoRef, overlayRef, again]);
  return box;
}

/**
 * A transport button for a feature a guest needs a free login for (Shuffle, Autoplay). It stays clickable: the player's own
 * setter shows the "Create a free login" box for a guest and changes nothing, so the button is only dimmed for a guest.
 */
function LockedTransportBtn({ locked, onClick, testId, title, active, hideOnPhone = false, children }) {
  return (
    <div className={`relative ${hideOnPhone ? "hidden sm:inline-flex" : "inline-flex"}`}>
      <button
        data-testid={testId}
        onClick={onClick}
        title={title}
        className={`steel-btn w-7 h-7 sm:w-8 sm:h-8 ${active ? "is-lit" : "is-dim"} ${locked ? "opacity-40" : ""}`}
      >
        {children}
      </button>
    </div>
  );
}

function SettingRow({ testId, icon: Icon, label, hint, checked, onToggle, locked }) {
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={testId}
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 text-left ${locked ? "opacity-50" : ""}`}
      >
        <Icon className={`w-4 h-4 shrink-0 ${checked ? "text-cyan-400" : "text-zinc-500"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{label}</div>
          <div className="text-[11px] text-zinc-500 truncate">{hint}</div>
        </div>
        <span
          aria-hidden="true"
          className={`relative shrink-0 w-9 h-5 rounded-full transition-colors ${checked ? "bg-cyan-400" : "bg-white/15"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : ""}`} />
        </span>
      </button>
    </div>
  );
}

/**
 * Transport bar shared by mini-bar and fullscreen overlay.
 * All controls (play/pause, prev, next, shuffle, repeat, autoplay, volume, settings) always visible.
 * On a phone the buttons are a little smaller so the song title keeps some room (it used to be
 * squeezed to a few letters). `cinema` is the phone-on-its-side layout: the title is already in
 * the bar at the top, so this bar is just the buttons and a seek bar.
 */
function TransportBar({ p, pct, showSettings, setShowSettings, settingsRef, variant, cinema = false, tv = false, onAdd = null, onInfo = null }) {
  const isFs = variant === "fullscreen";
  const CardTag = onInfo ? "button" : "div";   // the song card is a button that opens the song's description and lyrics
  // the sideways picture and TV mode both show the title in the bar at the top, so this bar is just buttons
  const lean = cinema || tv;
  const scopeId = isFs ? "fs-" : "";
  const guest = p.isGuest;   // Shuffle, Autoplay, Previous and Next need a free login (dimmed for a guest, and still open the login box); for a free member Shuffle and Autoplay are also dimmed unless a playlist or Favorites is playing (the player says they are only for playlists and favorites; Premium has them everywhere); Repeat is free for everyone
  return (
    // The little bar at the bottom runs the whole width of the screen, so the song card sits at the far left and the
    // volume buttons at the far right. The big player's bar does the same on a wide screen (xl), and keeps its
    // 1600px-wide middle on smaller ones and in the sideways / TV layouts.
    <div className={`px-4 sm:px-7 ${isFs ? (lean ? "max-w-[1600px]" : "max-w-[1600px] xl:max-w-none") : ""} ${cinema ? "h-20 steel-plate relative mx-2 mb-2 xl:mx-auto" : tv ? "h-28 mx-auto" : isFs ? "h-20 mx-auto" : "h-20"} flex items-center gap-2 sm:gap-6`}>
      {isFs ? (
        <>
          {/* The song card (thumbnail and title). Pressing it opens the song's description and lyrics. */}
          <CardTag {...(onInfo ? { type: "button", onClick: onInfo, title: "Read about this song", "data-testid": "fs-song-card" } : {})}
            className={`${lean ? "hidden" : "hidden sm:flex xl:w-60"} items-center gap-3 min-w-0 w-64${onInfo ? " text-left hover:bg-white/5 rounded-md transition-colors cursor-pointer" : ""}`}>
            <img src={p.current.cover_url} alt="" className="w-14 h-14 rounded-md object-cover shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{p.current.title}</div>
              <div className="text-xs text-zinc-400 truncate">{p.current.artist}</div>
            </div>
          </CardTag>
          {/* On a wide screen the + and Share live here, centered between the song card and the progress line, the same
              as in the little bar. (Smaller screens, sideways and TV mode keep them at the top left of the player.) */}
          {!lean && onAdd && (
            <span data-testid="fs-actions" className="hidden xl:flex items-center justify-center gap-2.5 relative left-[1.15rem] flex-1 min-w-[5.25rem] shrink-0">
              <button type="button" data-testid="fs-add-playlist-btn" onClick={onAdd} aria-label="Add to playlist" title="Add to playlist" className="steel-btn w-9 h-9 shrink-0">
                <SteelFace glyph="plus" />
              </button>
              <button type="button" data-testid="fs-share-btn" onClick={() => shareSong(p.current)} aria-label="Share" title="Share this song" className="steel-btn w-9 h-9 shrink-0">
                <SteelFace glyph="share" />
              </button>
              {onInfo && (
                <button type="button" data-testid="fs-info-btn" onClick={onInfo} aria-label="About this song" title="Read about this song" className="steel-btn w-9 h-9 shrink-0">
                  <SteelFace glyph="info" />
                </button>
              )}
            </span>
          )}
          {/* Sideways on a wide phone the right-hand volume block shows, so give the left an empty
              block of the same width and the buttons stay in the middle of the screen. */}
          {lean && <div aria-hidden="true" className="hidden md:block w-64 min-w-0" />}
        </>
      ) : (
        // On a wide screen (xl, 1280 and up) this block "dissolves" (xl:contents): the song card goes to the far left of
        // the bar and the + and Share buttons become their own piece of the bar, centered in the open space between the
        // card and where the time and progress line begin. Below that there is no spare room, so the + stays in front of
        // the picture as before (and Share is only in the big player).
        <div className="relative flex items-center gap-2 lg:gap-3.5 flex-1 min-w-0 sm:flex-initial sm:w-64 xl:contents">
          <button
            type="button"
            data-testid="mini-reopen-btn"
            onClick={() => p.setExpanded(true)}
            className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 text-left hover:bg-white/5 rounded-md transition-colors xl:flex-none xl:w-60"
            title="Reopen video"
          >
            <img src={p.current.cover_url} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-md object-cover shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{p.current.title}</div>
              <div className="text-xs text-zinc-400 truncate">{p.current.artist}</div>
            </div>
          </button>
          {/* On a phone there is no room beside the picture (the song title would be squeezed), so the + sits on the
              picture's top-left corner; from a small tablet up it stands in front of the picture (order-first). */}
          {onAdd && (
            <span data-testid="mini-actions" className="absolute -left-2 -top-2 z-[1] flex items-center sm:static sm:z-auto sm:order-first shrink-0 xl:order-none xl:relative xl:top-0 xl:left-[1.15rem] xl:flex-1 xl:min-w-[5.25rem] xl:justify-center xl:gap-2.5">
              <button type="button" data-testid="mini-add-playlist-btn" onClick={onAdd} aria-label="Add to playlist" title="Add to playlist" className="steel-btn w-7 h-7 sm:w-9 sm:h-9 shrink-0">
                <SteelFace glyph="plus" />
              </button>
              <span className="hidden xl:flex xl:items-center xl:gap-2.5">
                <button type="button" data-testid="mini-share-btn" onClick={() => shareSong(p.current)} aria-label="Share" title="Share this song" className="steel-btn w-9 h-9 shrink-0">
                  <SteelFace glyph="share" />
                </button>
                {onInfo && (
                  <button type="button" data-testid="mini-info-btn" onClick={onInfo} aria-label="About this song" title="Read about this song" className="steel-btn w-9 h-9 shrink-0">
                    <SteelFace glyph="info" />
                  </button>
                )}
              </span>
            </span>
          )}
        </div>
      )}
      <div className={`${isFs ? (lean ? "flex-1" : "flex-1 xl:flex-none xl:w-[28rem]") : "flex-none sm:flex-1 xl:flex-none xl:w-[28rem]"} flex flex-col items-center gap-1`}>
        <div className="flex items-center gap-2 sm:gap-3 md:gap-2 lg:gap-3">
          {/* Empty spacer, the width of one small button. Pause/Play has two buttons on its left
              and three on its right (Next, Repeat, Autoplay), so without this it sits left of
              the middle. With it, both sides match and Pause is the exact center of the row. */}
          <span aria-hidden="true" data-testid={`${scopeId}player-controls-spacer`} className={`${isFs ? "block" : "hidden sm:block"} w-7 sm:w-8 shrink-0`} />
          <LockedTransportBtn
            locked={guest || !p.apOpen}
            testId={`${scopeId}player-shuffle-btn`}
            title={guest || p.apOpen ? "Shuffle" : "Shuffle: these features only work in playlists and favorites"}
            active={p.shuffle}
            onClick={() => p.setShuffle(!p.shuffle)}
          >
            <SteelFace glyph="shuffle" />
          </LockedTransportBtn>
          <button data-testid={`${scopeId}player-prev-btn`} onClick={p.prev} title="Previous" aria-label="Previous" className={`steel-btn w-8 h-8 sm:w-9 sm:h-9 ${guest ? "is-dim opacity-40" : ""}`}><SteelFace glyph="prev" bevel /></button>
          <button data-testid={`${scopeId}player-toggle-btn`} onClick={p.toggle} title={p.playing ? "Pause" : "Play"} aria-label={p.playing ? "Pause" : "Play"} className="steel-btn steel-btn--primary w-10 h-10 sm:w-11 sm:h-11">
            <SteelFace glyph={p.playing ? "pause" : "play"} bevel />
          </button>
          <button data-testid={`${scopeId}player-next-btn`} onClick={p.next} title="Next" aria-label="Next" className={`steel-btn w-8 h-8 sm:w-9 sm:h-9 ${guest ? "is-dim opacity-40" : ""}`}><SteelFace glyph="next" bevel /></button>
          <LockedTransportBtn
            testId={`${scopeId}player-repeat-btn`}
            title="Repeat playlist"
            active={p.repeat}
            onClick={() => p.setRepeat(!p.repeat)}
          >
            <SteelFace glyph="repeat" />
          </LockedTransportBtn>
          {/* Autoplay toggle: in the big player always; in the little bar from tablet width up. On a phone
              the little bar has no room for it (it squeezed the song name), and the big player has it. */}
          <LockedTransportBtn
            locked={guest || !p.apOpen}
            hideOnPhone={!isFs}
            testId={`${scopeId}player-autoplay-btn`}
            title={guest || p.apOpen ? (p.autoplay ? "Autoplay next: ON" : "Autoplay next: OFF") : "Autoplay next: these features only work in playlists and favorites"}
            active={p.autoplay}
            onClick={() => p.setAutoplay(!p.autoplay)}
          >
            <SteelFace glyph="autoplay" />
          </LockedTransportBtn>
        </div>
        <div className={`${lean ? "flex" : "hidden sm:flex"} items-center gap-2 text-xs text-zinc-300 font-mono-ui w-full ${tv ? "max-w-2xl" : "max-w-md"}`}>
          <span>{fmt(p.progress)}</span>
          {/* The clickable strip is taller than the thin line so a thumb can hit it */}
          <div className="flex-1 py-2 -my-1 cursor-pointer" data-testid={`${scopeId}player-seek-bar`} onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            p.seek(((e.clientX - r.left) / r.width) * 100);
          }}>
            <div className="h-1.5 rounded-full steel-track">
              <div className="h-full rounded-full steel-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <span>{fmt(p.duration)}</span>
        </div>
      </div>
      {/* On a wide screen the little bar has an equal empty space on each side of the controls: the + and Share buttons
          fill the left one, and this fills the right one, so the controls stay in the middle of the screen. */}
      {!lean && <div aria-hidden="true" className="hidden xl:block flex-1 min-w-[5.25rem]" />}
      <div className={`hidden md:flex items-center gap-2 lg:gap-3 w-64 min-w-0 justify-end pr-1${lean ? "" : " xl:w-60"}`}>
        {/* "flex" so the button is the only thing inside; as a plain block the line-height left a few pixels under
            the button and the gear sat higher than the buttons beside it. */}
        <div className="relative flex" ref={settingsRef}>
          <button
            data-testid={`${scopeId}player-settings-btn`}
            onClick={() => setShowSettings((s) => !s)}
            title="Playback settings"
            aria-label="Playback settings"
            className={`steel-btn w-8 h-8 ${showSettings ? "is-lit" : ""}`}
          >
            <SteelFace glyph="settings" />
          </button>
          {showSettings && (
            <div
              data-testid={`${scopeId}player-settings-panel`}
              className="absolute bottom-full right-0 mb-2 w-64 glass border border-cyan-400/25 rounded-xl shadow-2xl p-3 space-y-1 z-[60]"
            >
              <div className="text-[10px] font-mono-ui uppercase tracking-widest text-zinc-500 px-2 pt-1 pb-2">Playback</div>
              <SettingRow testId={`${scopeId}setting-autoplay`} icon={PlayCircle} label="Autoplay next" hint="Play the next song when this one ends" checked={p.autoplay} onToggle={() => p.setAutoplay(!p.autoplay)} locked={guest || !p.apOpen} />
              <SettingRow testId={`${scopeId}setting-shuffle`} icon={Shuffle} label="Shuffle" hint="Pick a random next song from the queue" checked={p.shuffle} onToggle={() => p.setShuffle(!p.shuffle)} locked={guest || !p.apOpen} />
              <SettingRow testId={`${scopeId}setting-repeat`} icon={Repeat} label="Repeat playlist" hint="Loop the queue indefinitely" checked={p.repeat} onToggle={() => p.setRepeat(!p.repeat)} />
            </div>
          )}
        </div>
        <button data-testid={`${scopeId}player-mute-btn`} onClick={() => p.setMuted(!p.muted)} aria-label={p.muted ? "Unmute" : "Mute"} className="steel-btn w-8 h-8">
          <SteelFace glyph={p.muted ? "mute" : "volume"} />
        </button>
        <input type="range" min="0" max="1" step="0.05" value={p.muted ? 0 : p.volume}
          onChange={(e) => { p.setMuted(false); p.setVolume(parseFloat(e.target.value)); }}
          className="w-16 lg:w-20 accent-cyan-400" />
        <button data-testid={`${scopeId}player-expand-btn`} onClick={() => p.setExpanded(!p.expanded)} aria-label={p.expanded ? "Shrink player" : "Expand player"} className="steel-btn w-8 h-8">
          <SteelFace glyph={p.expanded ? "minimize" : "maximize"} />
        </button>
      </div>
    </div>
  );
}

// TV mode's "Up next" column: the next few songs in the list, big enough to read from the couch.
// With Shuffle on the next song is picked at random, so it cannot be listed.
function UpNext({ p }) {
  const q = p.queue || [];
  const items = [];
  if (!p.shuffle) {
    for (let k = 1; k < q.length && items.length < 4; k++) {
      let i = p.index + k;
      if (i >= q.length) { if (!p.repeat) break; i %= q.length; }
      items.push({ v: q[i], k: i });
    }
  }
  let note = null;
  if (p.shuffle) note = "Shuffle is on, so the next song is a surprise.";
  else if (!items.length) note = q.length > 1 ? "That is the last song in this list." : "This is the only song in the list.";
  return (
    <aside data-testid="tv-upnext" className="dtm-un hidden lg:flex flex-col w-80 xl:w-96 shrink-0 min-h-0 overflow-hidden py-4 pr-6 relative z-[1]">
      <div className="font-mono-ui text-base uppercase tracking-[0.3em] text-zinc-400 mb-4">Up next</div>
      {note && <div data-testid="tv-upnext-note" className="text-lg text-zinc-300">{note}</div>}
      <ul className="space-y-3">
        {/* On a short screen fewer songs show (and smaller), so none is ever cut off (index.css, .dtm-un). */}
        {items.map(({ v, k }) => (
          <li key={`${v.id}#${k}`} className="dtm-un-item">
            <button type="button" data-testid={`tv-upnext-${k}`} onClick={() => p.playQueue(q, k, { playlist: p.fromPlaylist })}
              className="w-full flex items-center gap-4 text-left p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-cyan-400/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300">
              <img src={v.cover_url} alt="" className="dtm-un-cover rounded-xl object-cover shrink-0 bg-zinc-800" />
              <span className="min-w-0">
                <span className="dtm-un-title block font-display font-semibold truncate">{v.title}</span>
                <span className="block text-base text-zinc-400 truncate">{v.artist}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// Keys on a TV remote (or a keyboard) in TV mode. Smart-TV browsers send their own key names and codes.
const TV_KEYS = {
  playpause: (e) => e.key === " " || e.key === "Spacebar" || e.key === "MediaPlayPause" || e.keyCode === 179,
  play: (e) => e.key === "MediaPlay" || e.keyCode === 415,
  pause: (e) => e.key === "MediaPause" || e.keyCode === 19,
  back10: (e) => e.key === "ArrowLeft" || e.key === "MediaRewind" || e.keyCode === 412,
  fwd10: (e) => e.key === "ArrowRight" || e.key === "MediaFastForward" || e.keyCode === 417,
  volUp: (e) => e.key === "ArrowUp",
  volDown: (e) => e.key === "ArrowDown",
  next: (e) => e.key === "MediaTrackNext" || e.keyCode === 176 || e.key === "n" || e.key === "N" || e.key === "PageDown",
  prev: (e) => e.key === "MediaTrackPrevious" || e.keyCode === 177 || e.key === "p" || e.key === "P" || e.key === "PageUp",
  mute: (e) => e.key === "m" || e.key === "M",
};
const TV_SEEK_SECONDS = 10;
const TV_HUD_MS = 1300;
// "Back" on a remote: Esc on a keyboard, and the codes Samsung (Tizen) and LG (webOS) TVs send.
const isBackKey = (e) => e.key === "Escape" || e.key === "GoBack" || e.key === "BrowserBack" || e.keyCode === 10009 || e.keyCode === 461;

export default function MiniPlayer() {
  const p = usePlayer();
  const pct = useMemo(() => (p.duration ? (p.progress / p.duration) * 100 : 0), [p.progress, p.duration]);
  const [showSettingsMini, setShowSettingsMini] = useState(false);
  const [showSettingsFs, setShowSettingsFs] = useState(false);
  const settingsRefMini = useRef(null);
  const settingsRefFs = useRef(null);
  const hiddenHostRef = useRef(null);
  const videoRefLocal = useRef(null);
  const overlayRef = useRef(null);
  // Captions: the song's timed lines go on the picture, and the CC button turns them on and off (only songs that have them).
  const captions = useCaptions(p);

  // Phone on its side, or full screen on any device: the video fills the whole screen and the controls
  // float on top of it (and fade away after a few seconds).
  const sideways = useSideways();
  const fullscreen = useFullscreen();
  // TV mode: Full screen for a TV across the room. The picture stays in its steel frame with the lights
  // glowing around it (not edge to edge), the text and buttons are big, and an "Up next" column shows.
  const [tv, setTv] = useState(false);
  const tvRef = useRef(false);
  tvRef.current = tv;
  const cinema = sideways || (fullscreen.active && !tv);
  // In every big-player layout (normal, sideways, Full screen, TV) the song name, the buttons and the bottom bar fade
  // away a few seconds after a song starts playing, and only the picture, the lights and the effects stay.
  const autoHide = cinema || tv || p.expanded;
  const [controlsShown, setControlsShown] = useState(true);
  const hideTimerRef = useRef(null);
  const pRef = useRef(p);
  pRef.current = p;
  const [, setAskTick] = useState(0);

  // Fit / Fill (edge-to-edge picture only). Automatic by default: a picture almost the shape of the screen
  // fills it, any other shape is shown whole. The Fit / Fill button overrides that for the song on screen.
  //   fitModeRef: null = automatic, "fill" or "whole" = the person's choice (forgotten when the song changes)
  //   fitNowRef: the picture-fitting function of the effect below, so the button can run it again
  const fitModeRef = useRef(null);
  const fitNowRef = useRef(null);
  const [filled, setFilled] = useState(false);
  const toggleFit = useCallback(() => {
    const v = videoRefLocal.current;
    const nowFilled = !!v && v.getAttribute("data-fill") === "cover";
    fitModeRef.current = nowFilled ? "whole" : "fill";
    if (fitNowRef.current) fitNowRef.current();
  }, []);
  const songId = p.current ? p.current.id : null;
  useEffect(() => {
    fitModeRef.current = null;
    if (fitNowRef.current) fitNowRef.current();
  }, [songId]);

  const wakeControls = useCallback(() => {
    setControlsShown(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsShown(false), CONTROLS_HIDE_MS);
  }, []);

  // The TV on the About page: a song started from it plays inside its screen (see lib/tvScreen.js), and the big player stays closed.
  const [tvHost, setTvHost] = useState(() => getTvScreenHost());
  useEffect(() => { setTvHost(getTvScreenHost()); return subscribeTvScreen(setTvHost); }, []);
  const inTv = !p.expanded && !!p.inlineTv && !!tvHost;
  // the <video>'s own hidden place, so the TV can hand it back at once when its screen leaves the page
  const songLoaded = !!p.current;
  useLayoutEffect(() => { setVideoHome(hiddenHostRef.current); return () => setVideoHome(null); }, [songLoaded]);

  // Imperatively move the <video> DOM node between the hidden host and the
  // fullscreen slot when `expanded` changes. Because React never rewrites this
  // subtree, the media element is preserved (playback/source state kept).
  // In the big player (not sideways) the video box takes the picture's own shape, so the steel
  // screen frame hugs the picture instead of a big empty black box. The box is as wide as the
  // slot allows, but never so wide that it would be taller than the slot (a tall picture gets
  // narrower instead of spilling out). cqw/cqh are the slot's own width/height.
  useLayoutEffect(() => {
    const video = videoRefLocal.current;
    if (!video) return undefined;
    const target = p.expanded
      ? document.getElementById("dtm-fullscreen-video-slot")
      : (inTv ? tvHost : hiddenHostRef.current);
    if (target && video.parentNode !== target) {
      target.appendChild(video);
    }
    let fit = () => {};
    let watchBox = null;
    fitNowRef.current = null;
    if (p.expanded && cinema) {
      video.className = "w-full h-full max-w-full max-h-full object-contain bg-black";
      video.style.aspectRatio = "";
      video.style.width = "";
      // A picture that is almost the same shape as the screen is nudged to fill it (a thin trim
      // off the edges, no black strips). A picture that is a very different shape (tall phone
      // video, ultra-wide movie) is always shown whole.
      fit = () => {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const bw = video.clientWidth;
        const bh = video.clientHeight;
        let fill = false;
        if (vw && vh && bw && bh) {
          const a = vw / vh;
          const b = bw / bh;
          fill = 1 - Math.min(a, b) / Math.max(a, b) <= FILL_MAX_TRIM;
        }
        if (fitModeRef.current === "fill") fill = true;
        else if (fitModeRef.current === "whole") fill = false;
        video.style.objectFit = fill ? "cover" : "";
        video.setAttribute("data-fill", fill ? "cover" : "whole");
        setFilled(fill);
      };
      fitNowRef.current = fit;
      fit();
      video.addEventListener("loadedmetadata", fit);
      video.addEventListener("resize", fit);
      if (typeof ResizeObserver !== "undefined") {
        watchBox = new ResizeObserver(fit);
        watchBox.observe(video);
      }
    } else if (p.expanded) {
      video.className = "max-w-full max-h-full object-contain bg-black rounded-md steel-screen";
      fit = () => {
        const ratio = video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
        video.style.aspectRatio = String(ratio);
        // An older browser that does not know cqw/cqh ignores the second line and keeps "100%".
        video.style.width = "100%";
        video.style.width = `min(100cqw, calc(100cqh * ${ratio}))`;
      };
      fit();
      video.addEventListener("loadedmetadata", fit);
      video.addEventListener("resize", fit);
    } else if (inTv) {
      video.className = "";      // sized by about-tv.css (.atv-vid video)
      video.style.aspectRatio = "";
      video.style.width = "";
    } else {
      video.className = "hidden";
      video.style.aspectRatio = "";
      video.style.width = "";
    }
    if (!(p.expanded && cinema)) {
      video.style.objectFit = "";
      video.removeAttribute("data-fill");
    }
    video.setAttribute("data-testid", p.expanded ? "expanded-player-video" : (inTv ? "tv-player-video" : "hidden-player-video"));
    return () => {
      video.removeEventListener("loadedmetadata", fit);
      video.removeEventListener("resize", fit);
      if (watchBox) watchBox.disconnect();
      if (fitNowRef.current === fit) fitNowRef.current = null;
    };
  }, [p.expanded, cinema, inTv, tvHost]);

  // ---- Mood lights (see "Mood lights" above) ----
  const { user } = useAuth() || {};
  const isPremium = !!(user && (user.is_premium || user.role === "admin"));    // (still decides how many playlists a member can fill)
  // Everyone (guests, free members and Premium) sees the lights the admin set for each song's mood, boom included, on every song
  // and on preview clips. Any logged-in member (free or Premium) can pick their own colors in the light bulb's panel;
  // a guest who taps the bulb gets the "Create a free login" box.
  const canPickLights = !!user;
  const moodLights = useMoodLights(true);
  const mine = useMyLights(canPickLights);
  const songScheme = useMemo(() => songLights(p.current, moodLights), [p.current, moodLights]);
  // what this member chose for this song, else their usual lights
  const myLights = useMemo(() => lightsFor(mine.prefs, songId), [mine.prefs, songId]);
  const scheme = useMemo(() => {
    if (!p.current) return null;
    if (!canPickLights) return songScheme && songScheme.style !== "off" ? songScheme : null;
    if (myLights.mode === "off") return null;
    const s = myLights.mode === "custom" || p.current.local ? normScheme(myLights.custom || STANDARD_SCHEME) : songScheme;
    return s && s.style !== "off" ? s : null;
  }, [canPickLights, p.current, myLights.mode, myLights.custom, songScheme]);
  // the steel buttons and plates everywhere on the page take the lights' first color
  const lightHue = scheme ? scheme.hues[0] : null;
  useEffect(() => {
    if (lightHue === null) return undefined;
    const vars = themeVars({ hues: [lightHue] });
    const root = document.documentElement;
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    return () => Object.keys(vars).forEach((k) => root.style.removeProperty(k));
  }, [lightHue]);
  // The TV on the About page glows with the same lights as the song (lib/lightsBus.js): the colors, the style, the effect.
  // Nothing is published while no song is loaded or the lights do not apply; { off: true } when a member switched their lights off.
  const lightsOff = canPickLights && !!p.current && myLights.mode === "off";
  useEffect(() => {
    if (scheme) {
      const beat = scheme.speed === BEAT && scheme.style !== "steady";
      const f = (SPEED_FACTOR[scheme.style] || SPEED_FACTOR.steady)[scheme.speed - 1] || 1;
      publishLights({
        style: scheme.style, glow: scheme.glow, fx: scheme.fx, beat, reach: scheme.reach || REACH_DEFAULT,
        per: beat ? BEAT_STEADY_PERIOD : (scheme.style === "heartbeat" ? 1.3 : 2.8) / f,
        spin: 6 / f,
        rgb: [0, 1, 2].map((i) => schemeColor(scheme, i)),
      });
    } else publishLights(lightsOff ? { off: true } : null);
  }, [scheme, lightsOff]);
  useEffect(() => () => publishLights(null), []);
  // a song playing inside the TV's screen with Beat lights: the TV gets each beat of the real music (see useTvBeats)
  const tvBeatOn = inTv && p.playing && !!scheme && scheme.speed === BEAT && scheme.style !== "steady";
  useTvBeats(tvBeatOn, videoRefLocal, p.current && p.current.id);
  const [lightsOpen, setLightsOpen] = useState(false);
  // The light bulb on the About page opens the same Lights panel while the big player is closed (lib/lightsBus.js).
  const [miniLights, setMiniLights] = useState(false);
  const haveSong = !!p.current;
  useEffect(() => onLightsPanelRequest(() => {
    if (!haveSong || !canPickLights || p.expanded) return false;
    setMiniLights(true);
    return true;
  }), [haveSong, canPickLights, p.expanded]);
  useEffect(() => { if (p.expanded || !haveSong || !canPickLights) setMiniLights(false); }, [p.expanded, haveSong, canPickLights]);
  const [capsOpen, setCapsOpen] = useState(false);          // the caption settings panel
  const [infoOpen, setInfoOpen] = useState(false);        // the song's description (the square i button)
  const toggleInfo = () => setInfoOpen((o) => !o);
  useEffect(() => { if (!haveSong) setInfoOpen(false); }, [haveSong]);
  useEffect(() => { setInfoOpen(false); }, [p.expanded]);
  const [teaserOpen, setTeaserOpen] = useState(false);     // the live sample + sign up, for guests and free members
  const [plSong, setPlSong] = useState(null);                // the song the "Add to playlist" list is open for (kept even if the next song starts)
  useEffect(() => { if (!p.expanded || cinema) { setLightsOpen(false); setTeaserOpen(false); } }, [p.expanded, cinema]);
  useEffect(() => { if (!p.expanded) setCapsOpen(false); }, [p.expanded]);
  const [miniPl, setMiniPl] = useState(null);                // the song the box is open for when it was opened from the little bar
  useEffect(() => { if (!p.expanded) setPlSong(null); else setMiniPl(null); }, [p.expanded]);
  const addFromBar = () => { if (user) setMiniPl(p.current); else askForLogin("playlist"); };
  const addFromPlayer = () => { if (user) setPlSong(p.current); else askForLogin("playlist"); };
  // Any touch, key or mouse movement brings the buttons back. They always stay up while the song is paused or while one of
  // the panels (playback settings, Lights, the sample pop-up, the playlist box) is open.
  const panelOpen = showSettingsFs || lightsOpen || capsOpen || infoOpen || teaserOpen || !!plSong;
  const controlsVisible = !autoHide || controlsShown || !p.playing || panelOpen;
  const panelOpenRef = useRef(false);
  panelOpenRef.current = panelOpen;
  const controlsVisibleRef = useRef(true);
  controlsVisibleRef.current = controlsVisible;
  const lightBox = useVideoBox(p.expanded && !cinema && !!scheme, videoRefLocal, overlayRef, p.current && p.current.id);

  // ---- Caption bar: the song's words on a thin steel plate that has its own room under (or above) the picture ----
  // The plate's light is the song's lights, what the Lights panel shows, or the person's own color (captions.style).
  const capText = useActiveLine(videoRefLocal, captions.cues, captions.on);
  const capStyle = captions.style;
  const capShown = p.expanded && captions.available && captions.on;
  const [vw, setVw] = useState(() => (typeof window === "undefined" ? 1024 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const capH = capStyle.size !== null ? capStyle.size : tv ? 45 : sideways ? 33 : cinema ? 44 : defaultBarSize(vw);
  const capHue = capStyle.color === "mine" ? capStyle.hue
    : capStyle.color === "match" ? (scheme ? scheme.hues[0] : STANDARD_SCHEME.hues[0])
      : (songScheme && songScheme.style !== "off" ? songScheme.hues[0] : STANDARD_SCHEME.hues[0]);
  const capRgb = hueColor(capHue);
  const capInk = capRgb[0] * 0.3 + capRgb[1] * 0.59 + capRgb[2] * 0.11 < 40;
  const capVars = useMemo(() => themeVars({ hues: [capHue] }), [capHue]);
  const capBox = useVideoBox(capShown && !cinema, videoRefLocal, overlayRef, `${p.current ? p.current.id : ""}|${capStyle.pos}|${capH}`);  // measure again when the bar moves above/below or changes size: the picture can move without changing its own size
  const capTop = capStyle.pos === "top";
  const capStrip = capShown && !cinema ? capH + 8 : 0;       // the room the plate takes under (or above) the picture; on a full screen the words go over the art instead, so nothing is taken
  useEffect(() => { if (!capShown) setCapsOpen(false); }, [capShown]);
  const capGeo = !capShown ? null
    : cinema ? { ...(capsOpen ? { left: 16, width: "calc(100% - 320px)" } : { left: "50%", transform: "translateX(-50%)", width: "min(calc(100% - 32px), 860px)" }), ...(capTop ? { top: sideways && !controlsVisible ? 12 : 64 } : { bottom: sideways && !controlsVisible ? 12 : 96 }) }
      : capBox ? { left: capBox.l, width: capBox.w, top: capTop ? capBox.t - 16 - capH : capBox.t + capBox.h + 16 }
        : null;
  // where the settings panel may go: the room on the other side of the picture from the bar, so the bar stays in view
  const capRoom = capBox && capGeo ? (capTop ? { top: capGeo.top + capH + 8, bottom: 112 } : { top: 64, bottom: Math.max(0, capBox.oh - capGeo.top + 8) }) : { top: 64, bottom: 112 };
  // how far a Beat blast may travel: the free room around the picture (a lot at the sides of a wide screen, a lot above and below on an upright phone)
  const blastRoom = useMemo(() => {
    if (!lightBox || !lightBox.ow) return null;
    return { x: Math.max(24, Math.min(lightBox.l, lightBox.ow - lightBox.l - lightBox.w)), y: Math.max(16, Math.min(lightBox.t, lightBox.oh - lightBox.t - lightBox.h)) };
  }, [lightBox]);
  // the free room on each side of the picture, out to the edges of the screen, so the effects can be seen all the way around it
  const fxBounds = useMemo(() => {
    if (!lightBox || !lightBox.ow) return null;
    return { l: Math.max(0, lightBox.l), r: Math.max(0, lightBox.ow - lightBox.l - lightBox.w), t: Math.max(0, lightBox.t), b: Math.max(0, lightBox.oh - lightBox.t - lightBox.h) };
  }, [lightBox]);
  const openLights = () => {
    if (canPickLights) setLightsOpen(true);
    else setTeaserOpen(true);
  };

  // Start the "hide the controls" countdown whenever a video starts playing in the big player, and again when a panel closes.
  useEffect(() => {
    if (autoHide && p.expanded && p.playing && !panelOpen) wakeControls();
    return () => clearTimeout(hideTimerRef.current);
  }, [autoHide, p.expanded, p.playing, panelOpen, wakeControls]);

  // Touch / mouse handling for the sideways screen. These are plain browser listeners on the
  // overlay (not React props) because the <video> is moved into the overlay by hand, and React
  // would not pass a tap on the video up to the overlay.
  useEffect(() => {
    const el = overlayRef.current;
    if (!el || !autoHide) return undefined;
    let lastType = "touch";
    const onDown = (e) => { lastType = e.pointerType || "touch"; };
    const onMove = (e) => { if (e.pointerType === "mouse") wakeControls(); };
    const onKey = () => wakeControls();                    // Tab and the like: a button may be about to get focus
    // Reacts to the finished tap ("click"), not to the finger touching down. If the controls
    // woke up on touch-down, the button that appeared under the thumb would catch the tap too
    // (a tap on the hidden Next button would skip the song).
    const onClick = (e) => {
      const onControls = e.target && e.target.closest && e.target.closest("[data-cinema-controls]");
      if (onControls || lastType === "mouse") { wakeControls(); return; }
      // a tap on the picture itself: show the controls, or tuck them away if they are showing
      if (controlsVisibleRef.current) {
        clearTimeout(hideTimerRef.current);
        setControlsShown(false);
      } else {
        wakeControls();
      }
    };
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [p.expanded, autoHide, wakeControls]);

  // Swipe down on the picture (or the song's name at the top) to close the big player, the way a phone or tablet closes a
  // full-screen player. It does what the X does: the song keeps playing in the little bar at the bottom, so a person can pick
  // another song. The picture follows the finger; past about 110 px (or a quick flick) it slides away and closes, otherwise it
  // springs back. Touch only (a mouse has the X), never over a button, a slider or an open panel, and not in TV mode.
  useEffect(() => {
    const el = overlayRef.current;
    if (!p.expanded || tv || !el) return undefined;
    let s = null;
    let timer = 0;
    const NOT_HERE = "button, a, input, select, textarea, [role='slider'], [role='dialog'], [data-testid='fullscreen-transport']";
    const settle = (ms) => { el.style.transition = `transform ${ms}ms ease-out, opacity ${ms}ms ease-out`; };
    const onDown = (e) => {
      s = null;
      if (e.pointerType !== "touch" || !e.isPrimary || panelOpenRef.current) return;
      if (e.target && e.target.closest && e.target.closest(NOT_HERE)) return;
      s = { id: e.pointerId, x: e.clientX, y: e.clientY, t: Date.now(), on: false };
    };
    const onMove = (e) => {
      if (!s || e.pointerId !== s.id) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (!s.on) {
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) { s = null; return; }      // a sideways wipe is not ours
        if (dy < 12) return;                                                               // a tap, or a swipe up
        s.on = true;
      }
      el.style.transition = "none";
      el.style.transform = `translateY(${Math.max(0, dy)}px)`;
      el.style.opacity = String(Math.max(0.35, 1 - dy / 500));
    };
    const onUp = (e) => {
      if (!s || e.pointerId !== s.id) return;
      const t = s;
      s = null;
      if (!t.on) return;
      const dy = e.clientY - t.y;
      const quick = dy / Math.max(1, Date.now() - t.t) > 0.6;                              // px per millisecond
      if (dy > 110 || (dy > 50 && quick)) {
        settle(160);
        el.style.transform = "translateY(100%)";
        el.style.opacity = "0";
        timer = setTimeout(() => { pRef.current.setExpanded(false); }, 170);
      } else {
        settle(180);
        el.style.transform = "";
        el.style.opacity = "";
        timer = setTimeout(() => { el.style.transition = ""; }, 200);
      }
    };
    const onCancel = () => {
      if (!s || !s.on) { s = null; return; }
      s = null;
      settle(180);
      el.style.transform = "";
      el.style.opacity = "";
    };
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onCancel);
    return () => {
      clearTimeout(timer);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onCancel);
      el.style.transition = "";
      el.style.transform = "";
      el.style.opacity = "";
    };
  }, [p.expanded, tv]);

  // Turning the phone on its side while something is playing pops the video open.
  const wasCinemaRef = useRef(sideways);
  useEffect(() => {
    const was = wasCinemaRef.current;
    wasCinemaRef.current = sideways;
    const cur = pRef.current;
    if (sideways && !was && cur.current && cur.playing && !cur.expanded) cur.setExpanded(true);
  }, [sideways]);

  // ---- TV mode ----
  // It begins with Full screen and ends when Full screen ends (the browser's own Esc, a TV's Back button),
  // when the big player closes, or with the TV button.
  const enterFullscreen = fullscreen.enter;
  const exitFullscreen = fullscreen.exit;
  const enterTv = useCallback(() => { setTv(true); enterFullscreen(); }, [enterFullscreen]);
  const exitTv = useCallback(() => { setTv(false); exitFullscreen(); }, [exitFullscreen]);
  const tvFsRef = useRef(false);
  useEffect(() => {
    if (!tv) { tvFsRef.current = false; return; }
    if (fullscreen.active) tvFsRef.current = true;
    else if (tvFsRef.current) { tvFsRef.current = false; setTv(false); }
  }, [tv, fullscreen.active]);
  useEffect(() => { if (!p.expanded) setTv(false); }, [p.expanded]);

  // The screen stays awake while TV mode is playing (a TV would otherwise dim or show its screensaver).
  useEffect(() => {
    if (!tv || !p.playing) return undefined;
    const wl = typeof navigator !== "undefined" ? navigator.wakeLock : null;
    if (!wl || typeof wl.request !== "function") return undefined;
    let lock = null;
    let busy = false;
    let gone = false;
    const grab = () => {
      if (gone || lock || busy || document.visibilityState === "hidden") return;
      busy = true;
      try {
        Promise.resolve(wl.request("screen")).then((s) => {
          busy = false;
          if (gone) { try { s.release(); } catch { /* already let go */ } return; }
          lock = s;
          if (s && s.addEventListener) s.addEventListener("release", () => { if (lock === s) lock = null; });
        }).catch(() => { busy = false; });
      } catch { busy = false; }
    };
    const onVisible = () => { if (document.visibilityState === "visible") grab(); };
    grab();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      gone = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (lock) {
        try { const r = lock.release(); if (r && typeof r.catch === "function") r.catch(() => {}); } catch { /* already let go */ }
        lock = null;
      }
    };
  }, [tv, p.playing]);

  // A small message at the top of the screen for what a key just did ("+10 s", "Volume 70%").
  const [hud, setHud] = useState(null);
  const hudTimerRef = useRef(null);
  const showHud = useCallback((text) => {
    setHud(text);
    clearTimeout(hudTimerRef.current);
    hudTimerRef.current = setTimeout(() => setHud(null), TV_HUD_MS);
  }, []);
  useEffect(() => () => clearTimeout(hudTimerRef.current), []);
  useEffect(() => { if (!tv) setHud(null); }, [tv]);

  // Remote / keyboard keys for TV mode. Space and Enter press whatever button has focus, unless the
  // buttons are faded out; then they play or pause. Arrows seek and change the volume.
  useEffect(() => {
    if (!tv) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isBackKey(e)) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[role="dialog"], [aria-modal="true"], [data-testid="upgrade-prompt"]')) return;
      const cur = pRef.current;
      const onButton = !!t && /^(BUTTON|A)$/.test(t.tagName) && controlsVisibleRef.current;
      const isEnter = e.key === "Enter";
      let handled = true;
      if ((TV_KEYS.playpause(e) || isEnter) && onButton && (e.key === " " || e.key === "Spacebar" || isEnter)) {
        handled = false;                                   // let the focused button take it
      } else if (TV_KEYS.playpause(e) || isEnter) {
        cur.toggle();
        showHud(cur.playing ? "Paused" : "Playing");
      } else if (TV_KEYS.play(e)) {
        if (!cur.playing) { cur.toggle(); showHud("Playing"); }
      } else if (TV_KEYS.pause(e)) {
        if (cur.playing) { cur.toggle(); showHud("Paused"); }
      } else if (TV_KEYS.back10(e) || TV_KEYS.fwd10(e)) {
        const d = TV_KEYS.fwd10(e) ? TV_SEEK_SECONDS : -TV_SEEK_SECONDS;
        if (cur.duration) cur.seek(Math.min(100, Math.max(0, ((cur.progress + d) / cur.duration) * 100)));
        showHud(d > 0 ? `+${TV_SEEK_SECONDS} s` : `−${TV_SEEK_SECONDS} s`);
      } else if (TV_KEYS.volUp(e) || TV_KEYS.volDown(e)) {
        const base = cur.muted ? 0 : cur.volume;
        const v = Math.min(1, Math.max(0, Math.round((base + (TV_KEYS.volUp(e) ? 0.1 : -0.1)) * 10) / 10));
        cur.setMuted(false);
        cur.setVolume(v);
        showHud(`Volume ${Math.round(v * 100)}%`);
      } else if (TV_KEYS.next(e)) {
        cur.next();
      } else if (TV_KEYS.prev(e)) {
        cur.prev();
      } else if (TV_KEYS.mute(e)) {
        cur.setMuted(!cur.muted);
        showHud(cur.muted ? "Sound on" : "Muted");
      } else {
        handled = false;
      }
      if (handled) e.preventDefault();
      wakeControls();                                      // any key brings the buttons back
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tv, wakeControls, showHud]);

  // Keys that work from anywhere on the site while a song is loaded (TV mode has its own keys, above):
  //   Space = play / pause, Right arrow = next song, Left arrow = previous song.
  // They stay out of the way of typing, sliders, menus and pop-ups. A focused button is still pressed by
  // the space bar as usual. Space only plays or pauses, it never opens the big player. Holding a key
  // down, or using Shift, Ctrl, Alt or the Windows key with it, does nothing here (Alt+Left is the browser's Back).
  const hasSong = !!p.current;
  useEffect(() => {
    if (!hasSong) return undefined;
    const TYPING = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="slider"], [role="spinbutton"], [role="textbox"], [role="combobox"], [role="tab"], [role="option"], [role="menuitem"], [role="radio"], [role="listbox"], [role="menu"], [role="tree"], [role="grid"]';
    const PRESSABLE = 'button, summary, [role="button"], [role="checkbox"], [role="switch"]';
    const onKey = (e) => {
      if (e.defaultPrevented || e.repeat || e.isComposing || tvRef.current) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const space = e.key === " " || e.key === "Spacebar";
      const right = e.key === "ArrowRight";
      const left = e.key === "ArrowLeft";
      if (!space && !right && !left) return;
      const t = e.target;
      if (t && t.closest) {
        if (t.closest(TYPING)) return;
        if (space && t.closest(PRESSABLE)) return;           // the space bar presses the focused button
      }
      if (document.querySelector('[role="dialog"], [aria-modal="true"], [role="menu"], [role="listbox"], [data-testid="upgrade-prompt"]')) return;
      e.preventDefault();                                   // the page must not scroll
      const cur = pRef.current;
      if (space) cur.setPlaying(!cur.playing);
      else if (right) cur.next();
      else cur.prev();
      wakeControls();                                       // (sideways / edge to edge: show the buttons for a moment)
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hasSong, wakeControls]);

  // Closing or shrinking the big player also leaves full screen.
  useEffect(() => {
    if (!p.expanded && fullscreen.active) exitFullscreen();
  }, [p.expanded, fullscreen.active, exitFullscreen]);

  // The Esc key backs out one step at a time. In Full screen the browser itself uses the first Esc
  // to leave Full screen (which also ends TV mode). The next Esc (big player, not full screen) closes
  // the big player down to the little bar; the song keeps playing. (A TV's Back button counts as Esc.)
  // It stays out of the way of anything else that uses Esc: the search sheet, menus, the Premium
  // pop-up, and typing in a box.
  const fsChangedAtRef = useRef(0);
  const fsWasRef = useRef(fullscreen.active);
  useEffect(() => {
    if (fsWasRef.current !== fullscreen.active) {
      fsWasRef.current = fullscreen.active;
      fsChangedAtRef.current = Date.now();
    }
  }, [fullscreen.active]);
  const playerOpen = p.expanded;
  useEffect(() => {
    if (!playerOpen) return undefined;
    const onKey = (e) => {
      if (!isBackKey(e) || e.defaultPrevented) return;
      if (e.key !== "Escape" && document.querySelector('[data-testid="captions-panel"], [data-testid="lights-panel"]')) return;   // a TV's Back closes the settings panel first
      if (fsElement()) {
        // the browser is using this Esc to leave Full screen. A TV's Back button it does not know, so do it here.
        if (e.key !== "Escape") exitFullscreen();
        return;
      }
      if (Date.now() - fsChangedAtRef.current < 400) return;    // ...and it just did: one Esc is one step
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[role="dialog"], [aria-modal="true"], [role="menu"], [role="listbox"], [data-testid="upgrade-prompt"]')) return;
      if (tvRef.current) { setTv(false); return; }              // TV mode without Full screen: leave TV mode first
      pRef.current.setExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [playerOpen, exitFullscreen]);

  useEffect(() => {
    if (!showSettingsMini && !showSettingsFs) return;
    const onDoc = (e) => {
      if (showSettingsMini && settingsRefMini.current && !settingsRefMini.current.contains(e.target)) setShowSettingsMini(false);
      if (showSettingsFs && settingsRefFs.current && !settingsRefFs.current.contains(e.target)) setShowSettingsFs(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showSettingsMini, showSettingsFs]);

  // the video refused to load when it asked for permission to be listened to: load it again without asking
  const onVideoError = (e) => {
    const v = e.currentTarget;
    if (!v || !v.hasAttribute("crossorigin") || v.readyState > 0) return;      // (an error part way through a song is not a refusal)
    const o = originOf(v.currentSrc || v.getAttribute("src") || "");
    if (!o) return;
    refused.set(o, Date.now());
    v.removeAttribute("crossorigin");
    setAskTick((n) => n + 1);
    // the player flips to "paused" when the first try fails; put it back once the video loads the second way
    const wanted = pRef.current.playing;
    const done = () => { v.removeEventListener("loadedmetadata", good); v.removeEventListener("error", bad); };
    const good = () => {
      done();
      if (!wanted) return;
      if (pRef.current.playing) v.play().catch(() => {});
      else pRef.current.setPlaying(true);
    };
    const bad = () => { done(); refused.delete(o); };         // it was not the refusal after all: ask again next time
    v.addEventListener("loadedmetadata", good);
    v.addEventListener("error", bad);
    v.load();
  };
  const setVideoRef = (el) => {
    videoRefLocal.current = el;
    if (typeof p.videoRef === "function") p.videoRef(el);
    else if (p.videoRef) p.videoRef.current = el;
  };

  if (!p.current) return null;

  return (
    <>
      {/* Fullscreen overlay: wraps video AND controls together */}
      {/* The three parts below (top bar, picture, bottom bar) are always the same three elements in
          the same order; sideways only changes their classes, so the video is never torn out. */}
      {p.expanded && (
        <div
          ref={overlayRef}
          className={`fixed inset-0 z-50 flex flex-col ${cinema ? "bg-black cursor-pointer" : "steel-backplate"}${tv ? " dtm-tv" : ""}${!cinema && !controlsVisible ? " cursor-none" : ""}`}
          data-testid="expanded-player"
          data-sideways={cinema ? "true" : "false"}
          data-tv={tv ? "true" : "false"}
          style={tv ? undefined : { touchAction: "none" }}
        >
          <div
            data-cinema-controls="true"
            className={cinema
              ? `absolute top-0 inset-x-0 z-10 flex items-center justify-between gap-3 px-4 pt-2 pb-8 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`
              : `flex items-center justify-between p-4 steel-headrule shrink-0 relative z-[2] transition-opacity duration-500 ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            style={cinema && capShown && capTop ? { top: capStrip } : undefined}
          >
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              {/* On a wide screen (xl) these two move down into the bottom bar, beside the song card. Smaller screens,
                  the sideways picture and TV mode have no room down there, so they stay here. */}
              {!p.current.local && (
              <div className={`flex items-center gap-2 sm:gap-3 shrink-0${!cinema && !tv ? " xl:hidden" : ""}`}>
                <button data-testid="add-playlist-btn" onClick={addFromPlayer} aria-label="Add to playlist" title="Add to playlist" className="steel-btn w-9 h-9 shrink-0">
                  <SteelFace glyph="plus" />
                </button>
                <button data-testid="player-share-btn" onClick={() => shareSong(p.current)} aria-label="Share" title="Share this song" className="steel-btn w-9 h-9 shrink-0">
                  <SteelFace glyph="share" />
                </button>
                <button data-testid="player-info-btn" onClick={toggleInfo} aria-label="About this song" title="Read about this song" className="steel-btn w-9 h-9 shrink-0">
                  <SteelFace glyph="info" />
                </button>
              </div>
              )}
              <div className="min-w-0">
                <div className={`font-display font-bold truncate ${cinema ? "text-base" : tv ? "text-3xl" : "text-xl"}`}>{p.current.title}</div>
                <div className={`text-zinc-400 truncate ${cinema ? "text-xs" : tv ? "text-xl" : "text-sm"}`}>{p.current.artist} {p.current.album ? `• ${p.current.album}` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {cinema && (
                <button data-testid="fit-fill-btn" onClick={toggleFit} data-mode={filled ? "fill" : "fit"}
                  aria-label={filled ? "Fit whole picture" : "Fill screen"} title={filled ? "Fit whole picture" : "Fill screen"}
                  className="steel-btn w-9 h-9 shrink-0">
                  <SteelFace glyph={filled ? "fit" : "fill"} />
                </button>
              )}
              {!sideways && (
                <div className="hidden md:flex">
                  <button data-testid="tv-mode-btn" onClick={tv ? exitTv : enterTv} aria-pressed={tv}
                    aria-label={tv ? "Exit TV mode" : "TV mode"} title={tv ? "Exit TV mode" : "TV mode"}
                    className={`steel-btn w-9 h-9 shrink-0${tv ? " is-lit" : ""}`}>
                    <SteelFace glyph="tv" />
                  </button>
                </div>
              )}
              {captions.available && (
                <button data-testid="captions-btn" onClick={captions.toggle} aria-pressed={captions.on}
                  aria-label={captions.on ? "Turn captions off" : "Turn captions on"} title={captions.on ? "Captions: on" : "Captions: off"}
                  className={`steel-btn w-9 h-9 shrink-0${captions.on ? " is-lit" : ""}`}>
                  <SteelFace glyph="cc" />
                </button>
              )}
              {!cinema && (
                <button data-testid="lights-btn" onClick={openLights} aria-label="Lights" title="Lights"
                  className={`steel-btn w-9 h-9 shrink-0${scheme ? " is-lit" : ""}`}>
                  <SteelFace glyph="bulb" />
                </button>
              )}
              {fullscreen.supported && !tv && (
                <button data-testid="fullscreen-toggle-btn" onClick={fullscreen.toggle} aria-pressed={fullscreen.active}
                  aria-label={fullscreen.active ? "Exit full screen" : "Full screen"} title={fullscreen.active ? "Exit full screen" : "Full screen"}
                  className="steel-btn w-9 h-9 shrink-0">
                  <SteelFace glyph={fullscreen.active ? "exitfs" : "fullscreen"} />
                </button>
              )}
              <button data-testid="collapse-player-btn" onClick={() => p.setExpanded(false)} aria-label="Close video" className="steel-btn w-9 h-9 shrink-0">
                <SteelFace glyph="close" />
              </button>
            </div>
          </div>
          {scheme && lightBox && !cinema && (
            <div className="absolute z-0 pointer-events-none" style={{ left: lightBox.l, top: lightBox.t, width: lightBox.w, height: lightBox.h }} data-testid="backlight-box">
              <Backlight scheme={scheme} paused={!p.playing} mediaRef={videoRefLocal} room={blastRoom} bounds={fxBounds} />
            </div>
          )}
          {/* The picture's slot, and in TV mode the Up next column beside it. (Edge to edge, the wrapper adds
              no box of its own, so the slot covers the whole screen as before.) */}
          <div className={cinema ? "contents" : "flex-1 min-h-0 flex"}>
            <div
              id="dtm-fullscreen-video-slot"
              className={cinema ? "absolute inset-0 flex items-center justify-center" : "flex-1 min-w-0 min-h-0 flex items-center justify-center p-4 relative z-[1]"}
              style={{
                containerType: "size",
                ...(!capShown ? {}
                  : cinema ? (capTop ? { top: capStrip } : { bottom: capStrip })
                    : (capTop ? { paddingTop: 16 + capStrip } : { paddingBottom: 16 + capStrip })),
              }}
            />
            {tv && <UpNext p={p} />}
          </div>
          {tv && hud && (
            <div data-testid="tv-hud" className="absolute top-8 left-1/2 -translate-x-1/2 z-20 px-6 py-3 rounded-full glass border border-white/15 text-2xl font-display font-semibold pointer-events-none">{hud}</div>
          )}
          {capGeo && (
            <CaptionBar text={capText} geo={capGeo} h={capH} vars={capVars} ink={capInk} words={capStyle.words} glow={capStyle.glow} over={cinema}
              fx={!cinema && capStyle.fx && scheme && BAR_FX[scheme.fx] ? scheme.fx : null} paused={!p.playing}
              visible={controlsVisible} onOpen={() => setCapsOpen(true)} />
          )}
          <div
            data-cinema-controls="true"
            style={cinema && capShown && !capTop ? { bottom: capStrip } : undefined}
            className={cinema
              ? `absolute bottom-0 inset-x-0 z-10 pt-8 bg-gradient-to-t from-black/85 via-black/60 to-transparent transition-opacity duration-300 ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`
              : `steel-deck shrink-0 relative z-10 transition-opacity duration-500 ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            data-testid="fullscreen-transport"
          >
            <TransportBar p={p} pct={pct} showSettings={showSettingsFs} setShowSettings={setShowSettingsFs} settingsRef={settingsRefFs} variant="fullscreen" cinema={cinema} tv={tv} onAdd={p.current.local ? null : addFromPlayer} onInfo={p.current.local ? null : toggleInfo} />
          </div>
          {lightsOpen && !cinema && (
            <LightsPanel
              onClose={() => setLightsOpen(false)} prefs={mine.prefs}
              songId={songId} songTitle={p.current.title || "this song"}
              updateSong={mine.updateSong} forgetSong={mine.forgetSong} applyToAll={mine.applyToAll}
              saveScheme={mine.saveScheme} deleteScheme={mine.deleteScheme}
              songScheme={songScheme} songMood={songMoodName(p.current)}
              mediaRef={videoRefLocal} playing={p.playing}
            />
          )}
          {capsOpen && capShown && (
            <CaptionsPanel style={capStyle} setStyle={captions.setStyle} barH={capH} hue={capHue} canMatch={canPickLights} codes={!!user}
              awayFromTop={capTop} sideways={cinema} strip={capStrip} room={capRoom} onClose={() => setCapsOpen(false)} />
          )}
          {infoOpen && (
            <SongInfoPanel song={p.current} onClose={() => setInfoOpen(false)} scheme={cinema ? null : scheme} playing={p.playing} mediaRef={videoRefLocal} />
          )}
          {plSong && user && (
            <AddToPlaylistPanel song={plSong} many={isPremium} onClose={() => setPlSong(null)} />
          )}
          {teaserOpen && !cinema && (
            <LightsTeaser guest={!user} onClose={() => setTeaserOpen(false)} onGo={() => { setTeaserOpen(false); p.setExpanded(false); }} />
          )}
        </div>
      )}

      <div className={`fixed bottom-0 left-0 right-0 z-40 steel-deck ${p.expanded ? "hidden" : ""}`} data-testid="mini-player">
        <div className="h-1 bg-white/5 relative cursor-pointer" onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          p.seek(((e.clientX - r.left) / r.width) * 100);
        }} data-testid="mini-progress-bar">
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-400 to-fuchsia-400" style={{ width: `${pct}%` }} />
        </div>
        <TransportBar p={p} pct={pct} showSettings={showSettingsMini} setShowSettings={setShowSettingsMini} settingsRef={settingsRefMini} variant="mini" onAdd={p.current.local ? null : addFromBar} onInfo={p.current.local ? null : toggleInfo} />
      </div>
      {infoOpen && !p.expanded && (
        <SongInfoPanel fixed song={p.current} onClose={() => setInfoOpen(false)} scheme={cinema ? null : scheme} playing={p.playing} mediaRef={videoRefLocal} />
      )}
      {miniPl && user && !p.expanded && (
        <AddToPlaylistPanel fixed song={miniPl} many={isPremium} onClose={() => setMiniPl(null)} />
      )}
      {miniLights && !p.expanded && (
        <div className="fixed inset-0 z-[80]" data-testid="mini-lights-host">
          <LightsPanel
            onClose={() => setMiniLights(false)} prefs={mine.prefs}
            songId={songId} songTitle={p.current.title || "this song"}
            updateSong={mine.updateSong} forgetSong={mine.forgetSong} applyToAll={mine.applyToAll}
            saveScheme={mine.saveScheme} deleteScheme={mine.deleteScheme}
            songScheme={songScheme} songMood={songMoodName(p.current)}
            mediaRef={videoRefLocal} playing={p.playing}
          />
        </div>
      )}

      <div ref={hiddenHostRef} aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
        <video
          ref={setVideoRef}
          crossOrigin={askForSound(p.current.video_url) ? "anonymous" : undefined}
          src={p.current.video_url}
          poster={p.current.cover_url}
          onTimeUpdate={p.onTimeUpdate}
          onLoadedMetadata={p.onLoadedMetadata}
          onEnded={p.onEnded}
          onError={onVideoError}
          controlsList="nodownload"
          playsInline
          onContextMenu={(e) => e.preventDefault()}
          data-testid="hidden-player-video"
          className="hidden"
        />
      </div>
    </>
  );
}
